import { BUS_LINES, COLLECTION_VERSION, duplicateQuestion, recordFlags } from "./collection.js";
import { assignAlternativeOrder, renderScenarioCards, orderedAlternatives } from "./scenario.js";
import { authenticate, changeRequiredPassword, restoreSession, signOut } from "./auth.js";
import {
  createManagedUser,
  listResearchers,
  resetManagedUserPassword,
  setManagedUserStatus,
  updateManagedUser
} from "./admin-users.js";
import { supabase, supabaseConfig, supabasePing } from "./supabase.js";
import { PASSWORD_REQUIREMENTS, PasswordValidationError, validateNewPassword } from "./password.js";
import {
  buildLocalBackup,
  claimNextBlock,
  dashboardSummary,
  enqueueDeletion,
  enqueueInterview,
  getLatestCompletedInterview,
  getSettings,
  migrateLegacyDatabase,
  openDatabase,
  recoverInterruptedSync,
  saveInterview,
  saveSettings
} from "./db.js";
import {
  filterNeighborhoodEntries,
  findFirstIncomplete,
  loadJson,
  optionLabel,
  readRequiredRadio,
  renderNeighborhoodOptions,
  renderLikertSection,
  renderRadioOptions,
  resolveNeighborhoodSelection,
  validateCompleteInterview,
  validateFieldContext,
  validateMobility,
  validateProfile,
  validatePsychometrics
} from "./form.js";
import { applyTheme, currentTheme, themeLabel, toggleTheme } from "./theme.js";
import { fetchAdminDashboard, fetchAllDeletionRequests, fetchAllInterviewDocuments, synchronizePending, testConnection } from "./sync.js";
import { buildAdminDashboard, buildChoiceAnalytics } from "./dashboard.js";
import { buildChoiceLongCsv, buildWideCsv } from "./export-data.js";
import {
  createUuid,
  deepClone,
  downloadJson,
  downloadText,
  durationSeconds,
  escapeHtml,
  formatDateTime,
  getCurrentLocation,
  localTimezoneOffset,
  normalizeText,
  nowIso,
  shortId,
  STATUS_LABELS
} from "./utils.js";

const app = document.querySelector("#app");
const toastRegion = document.querySelector("#toast-region");

const state = {
  experiment: null,
  questionnaire: null,
  neighborhoodCatalog: null,
  session: null,
  backendHealth: null,
  route: "startup",
  activeInterview: null,
  choiceIndex: 0,
  formError: "",
  busy: false,
  syncProgress: null,
  syncResult: null,
  adminDashboard: null,
  adminDocuments: null,
  adminDashboardLoading: false,
  adminDashboardError: "",
  adminLastRefresh: null,
  adminRealtimeActive: false,
  adminPhase: "all",
  adminRecordKind: "all",
  researchers: null,
  researchersLoading: false,
  researchersError: "",
  editingUserId: null,
  createUserModalOpen: false,
  adminAnalyticsVersion: null,
  adminAnalyticsGender: "all",
  adminAnalyticsTreatment: "all",
  installPrompt: null,
  installHelpVisible: false,
  lastSavedAt: null,
  draftSaveState: "saved"
};

const ADMIN_REFRESH_INTERVAL_MS = 60_000;
let adminRefreshTimer = null;
let autoSyncTimer = null;
let adminRealtimeSubscription = null;
let adminRealtimeGeneration = 0;
let draftSaveTimer = null;
let draftSaveChain = Promise.resolve();

const PSYCHOMETRIC_KINDS = ["security", "comfort", "trust"];
const STEP_LABELS = ["Contexto", "Perfil e hábitos", "Orientação", "Cenários", "Segurança", "Conforto", "Confiança", "Revisão"];
const ROUTE_STEP = Object.freeze({
  "survey-context": 0,
  "survey-profile": 1,
  "survey-intro": 2,
  "survey-choice": 3,
  "survey-mobility": 1,
  "survey-security": 4,
  "survey-comfort": 5,
  "survey-trust": 6,
  "survey-review": 7
});

function isAdminPortalRole(role = state.session?.role) {
  return role === "admin" || role === "superuser";
}

function isSuperuser() {
  return state.session?.role === "superuser";
}

function canRunSurveys(role = state.session?.role) {
  return role === "researcher" || role === "superuser";
}

function showToast(message, type = "info") {
  const toast = document.createElement("div");
  toast.className = `toast ${type}`;
  toast.textContent = message;
  toastRegion.append(toast);
  setTimeout(() => toast.remove(), 4200);
}

function showInlineFormError(form, message, input = null) {
  let alert = form.querySelector(".form-inline-error");
  if (!alert) {
    alert = document.createElement("p");
    alert.className = "form-error form-inline-error";
    alert.setAttribute("role", "alert");
    form.prepend(alert);
  }
  alert.textContent = message;
  alert.hidden = false;
  if (input) {
    input.setAttribute("aria-invalid", "true");
    input.focus({ preventScroll: true });
  }
}

function validationMessageForField(field) {
  if (field.type === "radio") {
    return field.closest(".likert-question")
      ? "Marque uma opção nesta afirmação."
      : "Marque uma das opções para continuar.";
  }
  if (field.type === "number") {
    const minimum = field.min || "o mínimo";
    const maximum = field.max || "o máximo";
    return `Informe um número entre ${minimum} e ${maximum}.`;
  }
  if (field.tagName === "SELECT") return "Selecione uma opção para continuar.";
  return field.validity?.valueMissing ? "Preencha este campo para continuar." : "Revise este campo.";
}

function showFieldAttention(field, message = validationMessageForField(field)) {
  const container = field.closest(".likert-question, .form-field") || field;
  container.classList.add("needs-attention");
  const existing = container.querySelector(":scope > .field-error");
  const error = existing || document.createElement("p");
  if (!existing) {
    error.className = "field-error";
    error.setAttribute("role", "alert");
    container.append(error);
  }
  error.textContent = message;
  field.setAttribute("aria-invalid", "true");
  container.scrollIntoView({ behavior: "smooth", block: "center" });
  field.focus({ preventScroll: true });
}

function clearFieldAttention(field) {
  field.removeAttribute("aria-invalid");
  const container = field.closest(".likert-question, .form-field");
  if (!container) return;
  container.classList.remove("needs-attention");
  const error = container.querySelector(":scope > .field-error");
  if (error) error.remove();
}

async function navigate(route) {
  if (state.activeInterview && state.route.startsWith("survey-") && route !== state.route) await flushDraftAutosave();
  state.route = route;
  state.formError = "";
  if (route !== "admin-users") state.createUserModalOpen = false;
  return render();
}

function connectionBadge() {
  return `<span class="connection-badge ${navigator.onLine ? "" : "offline"}"><span>${navigator.onLine ? "Online" : "Offline"}</span></span>`;
}

function topbar(title, subtitle = "", options = {}) {
  return `
    <header class="topbar">
      <div class="topbar-brand">
        <div class="brand-mark" aria-hidden="true">PD</div>
        <div>
          <div class="topbar-title">${escapeHtml(title)}</div>
          <div class="topbar-subtitle">${escapeHtml(subtitle)}</div>
        </div>
      </div>
      <div class="topbar-actions">
        ${connectionBadge()}
        ${themeButton("button topbar-button icon-button", true)}
        ${options.adminReturn ? `<button class="button topbar-button small" type="button" data-action="open-admin-portal">Painel</button>` : ""}
        ${options.survey ? `<button class="button topbar-button small" type="button" data-action="save-exit">Salvar e sair</button>${state.activeInterview?.editBaseRevision == null ? `<button class="button topbar-button small" type="button" data-action="abandon-interview">Encerrar parcial e sair</button>` : ""}` : ""}
        ${options.logout ? `<button class="button topbar-button small" type="button" data-action="logout">Sair</button>` : ""}
      </div>
    </header>
  `;
}

function appFrame(content, options = {}) {
  return `
    <div class="app-frame">
      ${topbar(options.title || "Experimento PD", options.subtitle || "", options)}
      ${content}
    </div>
  `;
}

function iconSvg(name) {
  const paths = {
    bus: '<path d="M5 16V6.5C5 4 7.3 3 12 3s7 1 7 3.5V16M5 11h14M8 19h8M7.5 16.5h.01M16.5 16.5h.01"/><path d="M7 19v2M17 19v2"/>',
    train: '<path d="M6 16V6c0-2 2-3 6-3s6 1 6 3v10c0 1.7-1.3 3-3 3H9c-1.7 0-3-1.3-3-3Z"/><path d="M6 11h12M9 7h.01M15 7h.01M9 19l-2 2M15 19l2 2"/>',
    clock: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7v5l3.5 2"/>',
    walk: '<circle cx="13" cy="5" r="2"/><path d="m10 21 1-6-3-2 2-5 3 2 3 1 2-2M13 10l-2 5 4 2 2 4"/>',
    crowd: '<circle cx="8" cy="8" r="2.5"/><circle cx="16" cy="8" r="2.5"/><circle cx="12" cy="5" r="2.5"/><path d="M3.5 19c.2-3 1.7-5 4.5-5s4.3 2 4.5 5M11.5 19c.2-3 1.7-5 4.5-5s4.3 2 4.5 5"/>',
    sun: '<circle cx="12" cy="12" r="3.5"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>',
    stairs: '<path d="M3 19h5v-5h5V9h5V4h3"/>',
    coin: '<circle cx="12" cy="12" r="8.5"/><path d="M15 8.5c-.8-.7-1.7-1-3-1-1.7 0-3 .8-3 2s1 1.8 3 2.3 3 1.1 3 2.4-1.3 2.3-3 2.3c-1.3 0-2.4-.4-3.2-1.2M12 5.5v13"/>',
    pin: '<path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/>',
    download: '<path d="M12 3v12M7 10l5 5 5-5M4 21h16"/>',
    users: '<circle cx="9" cy="8" r="3"/><path d="M3.5 20c.4-4 2.2-6 5.5-6s5.1 2 5.5 6M16 5.5a3 3 0 0 1 0 5.8M16.5 14c2.5.4 3.8 2.4 4 6"/>',
    chart: '<path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/>',
    database: '<ellipse cx="12" cy="5" rx="8" ry="3"/><path d="M4 5v7c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12v7c0 1.7 3.6 3 8 3s8-1.3 8-3v-7"/>',
    settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3v-.2h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z"/>',
    home: '<path d="m3 11 9-8 9 8M5 10v11h14V10M9 21v-7h6v7"/>',
    refresh: '<path d="M20 6v5h-5"/><path d="M19.2 11A7.5 7.5 0 1 0 19 16.2"/>',
    plus: '<path d="M12 5v14M5 12h14"/>',
    install: '<path d="M12 3v11M7.5 10 12 14.5 16.5 10M5 19h14"/>',
    moon: '<path d="M20.5 15.4A8.5 8.5 0 0 1 8.6 3.5a8.5 8.5 0 1 0 11.9 11.9Z"/>',
    search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/>',
    check: '<path d="m5 12 4 4L19 6"/>',
    eye: '<path d="M2.5 12s3.4-6 9.5-6 9.5 6 9.5 6-3.4 6-9.5 6-9.5-6-9.5-6Z"/><circle cx="12" cy="12" r="2.7"/>',
    eyeOff: '<path d="m3 3 18 18M10.6 6.1A9.7 9.7 0 0 1 12 6c6.1 0 9.5 6 9.5 6a15 15 0 0 1-2.2 3M6.4 6.4C3.9 8.1 2.5 12 2.5 12s3.4 6 9.5 6a9.7 9.7 0 0 0 3-.5M9.9 9.9a3 3 0 0 0 4.2 4.2"/>'
  };
  return `<svg class="ui-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${paths[name] || paths.clock}</svg>`;
}

function themeButton(className = "button secondary small", compact = false) {
  const dark = currentTheme() === "dark";
  return `<button class="${className}" type="button" data-action="toggle-theme" aria-label="${escapeHtml(themeLabel())}" title="${escapeHtml(themeLabel())}">
    ${iconSvg(dark ? "sun" : "moon")}<span${compact ? ' class="sr-only"' : ""}>${dark ? "Tema claro" : "Tema escuro"}</span>
  </button>`;
}

function passwordField({ id, name, label, autocomplete = "new-password", placeholder = "", autofocus = false }) {
  return `<div class="form-field full">
    <label for="${escapeHtml(id)}">${escapeHtml(label)}</label>
    <div class="password-control">
      <input id="${escapeHtml(id)}" name="${escapeHtml(name)}" type="password" autocomplete="${escapeHtml(autocomplete)}" data-password-policy="true" ${placeholder ? `placeholder="${escapeHtml(placeholder)}"` : ""} ${autofocus ? "autofocus" : ""} required>
      <button class="password-toggle" type="button" data-action="toggle-password" data-target="${escapeHtml(id)}" aria-label="Mostrar senha" aria-pressed="false">${iconSvg("eye")}<span>Mostrar</span></button>
    </div>
  </div>`;
}

function passwordRequirementHint() {
  return `<p class="password-requirements">${escapeHtml(PASSWORD_REQUIREMENTS.description)}</p>`;
}

function attributeIconName(attribute) {
  const id = String(attribute.id || "").toLowerCase();
  if (id.includes("crowd") || id.includes("lot")) return "crowd";
  if (id.includes("light") || id.includes("ilumin")) return "sun";
  if (id.includes("stair") || id.includes("escad")) return "stairs";
  if (id.includes("walk") || id.includes("caminh")) return "walk";
  if (id.includes("fare") || id.includes("tarif") || id.includes("cost")) return "coin";
  return "clock";
}

function attributeContextLabel(attribute) {
  if (attribute.context_label) return attribute.context_label;
  const id = String(attribute.id || "");
  if (id.includes("walk")) return "Trecho a pé";
  if (id.includes("wait")) return "Tempo de espera";
  return "Tempo de viagem";
}

function isInstalledApp() {
  return globalThis.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true;
}

function installHelp() {
  if (!state.installHelpVisible) return "";
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  return `<div class="install-help" role="status">
    <strong>${isIos ? "Instalar no iPhone ou iPad" : "Instalar neste dispositivo"}</strong>
    <p>${isIos
      ? "Abra esta página no Safari, toque em Compartilhar e escolha “Adicionar à Tela de Início”."
      : "Abra o menu do navegador e escolha “Instalar aplicativo” ou “Adicionar à tela inicial”."}</p>
  </div>`;
}

function installAction(className = "button secondary") {
  if (isInstalledApp()) return "";
  return `<div class="install-action">
    <button class="${className}" type="button" data-action="install-app">${iconSvg("install")} Instalar aplicativo</button>
    ${installHelp()}
  </div>`;
}

function renderLogin() {
  return `
    <main class="login-screen">
      <div class="login-theme">${themeButton()}</div>
      <section class="login-card">
        <div class="login-brand">
          <div class="brand-mark" aria-hidden="true">PD</div>
          <div><p class="eyebrow">PDP</p><h1>Pesquisa de preferência declarada</h1></div>
        </div>
        <div class="login-panel">
          <h2>Acessar plataforma</h2>
          <p>Entre com as credenciais fornecidas pelo administrador.</p>
          ${state.formError ? `<p class="form-error" role="alert">${escapeHtml(state.formError)}</p>` : ""}
          <form id="login-form" class="form-grid">
            <div class="form-field full">
              <label for="email">E-mail</label>
              <input id="email" name="email" type="email" autocomplete="username" autocapitalize="none" required autofocus>
            </div>
            <div class="form-field full">
              <label for="password">Senha</label>
              <div class="password-control">
                <input id="password" name="password" type="password" autocomplete="current-password" required>
                <button class="password-toggle" type="button" data-action="toggle-password" data-target="password" aria-label="Mostrar senha" aria-pressed="false">${iconSvg("eye")}<span>Mostrar</span></button>
              </div>
            </div>
            <div class="form-field full">
              <button class="button login-submit" type="submit">Entrar</button>
            </div>
          </form>
          ${installAction("button secondary install-button")}
          ${state.backendHealth && !state.backendHealth.ok ? `<div class="callout warning"><p>O serviço está temporariamente indisponível. Se esta conta já foi usada neste aparelho, o modo offline poderá ser restaurado.</p></div>` : ""}
        </div>
      </section>
      <p class="login-footer">PDP · Plataforma de coleta em campo</p>
    </main>
  `;
}

function renderRequiredPasswordChange() {
  return `
    <main class="login-screen">
      <div class="login-theme">${themeButton()}</div>
      <section class="login-card password-card">
        <div class="login-brand"><div class="brand-mark" aria-hidden="true">PD</div><div><p class="eyebrow">Primeiro acesso</p><h1>Crie sua senha</h1></div></div>
        <div class="login-panel">
          <h2>Proteja sua conta</h2>
          <p class="account-email">${escapeHtml(state.session?.email || "")}</p>
          <p>Como você acabou de entrar com a senha temporária, basta definir e confirmar sua senha pessoal.</p>
          ${state.formError ? `<p class="form-error" role="alert">${escapeHtml(state.formError)}</p>` : ""}
          <form id="forced-password-form" class="form-grid">
            ${passwordField({ id: "new-password", name: "newPassword", label: "Nova senha", autofocus: true })}
            ${passwordField({ id: "confirm-password", name: "confirmPassword", label: "Confirmar nova senha" })}
            <div class="form-field full">${passwordRequirementHint()}</div>
            <div class="form-field full"><button class="button" type="submit">Salvar nova senha</button></div>
          </form>
          <button class="button secondary" type="button" data-action="logout">Sair</button>
        </div>
      </section>
    </main>
  `;
}

function statusBadge(status, deleted = false) {
  if (deleted) return `<span class="status-badge deleted">Exclusão solicitada</span>`;
  return `<span class="status-badge ${escapeHtml(status)}">${escapeHtml(STATUS_LABELS[status] || status || "—")}</span>`;
}

async function renderResearcherHome() {
  const [summary, settings, latest] = await Promise.all([
    dashboardSummary(),
    getSettings(),
    getLatestCompletedInterview()
  ]);
  const draft = summary.interviews.find(interview => interview.status === "draft");
  const partials = summary.interviews.filter(interview => interview.status === "abandoned");
  const pendingAttention = summary.pending > 0;
  const lastSyncedAt = summary.interviews.map(interview => interview.lastSyncedAt).filter(Boolean).sort().at(-1);

  return appFrame(`
    <main class="page">
      <div class="page-heading">
        <div>
          <p class="eyebrow">${isSuperuser() ? "Coleta do superusuário" : "Portal do pesquisador"}</p>
          <h1>Coleta de entrevistas</h1>
          <p>${escapeHtml(state.session?.username || settings.interviewerCode)} · ${escapeHtml(settings.deviceId)}</p>
        </div>
        ${installAction("button secondary")}
      </div>

      ${draft ? `
        <div class="callout warning">
          <p><strong>Há uma entrevista em preenchimento.</strong> Ela foi salva neste dispositivo e pode ser retomada.</p>
          <button class="button small" type="button" data-action="resume-draft" data-id="${escapeHtml(draft.id)}">Continuar entrevista ${escapeHtml(shortId(draft.id))}</button>
        </div>
      ` : ""}

      <section class="metrics-grid" aria-label="Resumo local">
        <div class="metric-card"><span class="metric-label">Entrevistas concluídas</span><strong class="metric-value">${summary.total}</strong></div>
        <div class="metric-card ${pendingAttention ? "attention" : ""}"><span class="metric-label">Aguardando envio</span><strong class="metric-value">${summary.pending}</strong></div>
        <div class="metric-card"><span class="metric-label">Envios confirmados</span><strong class="metric-value">${summary.synced}</strong></div>
        <div class="metric-card"><span class="metric-label">Precisam de atenção</span><strong class="metric-value">${summary.errors}</strong></div>
      </section>

      <section class="actions-grid" aria-label="Ações principais">
        <button class="action-card primary" type="button" data-action="new-interview" ${state.busy || draft ? "disabled" : ""}>
          <span class="action-icon">＋</span>
          <h3>Iniciar entrevista</h3>
          <p>${draft ? "Continue a entrevista em andamento antes de iniciar outra." : "Abre um novo questionário e registra automaticamente identificação, data, hora e localização."}</p>
        </button>
        <button class="action-card" type="button" data-action="edit-latest" ${latest ? "" : "disabled"}>
          <span class="action-icon">✎</span>
          <h3>Editar última entrevista</h3>
          <p>${latest ? `${escapeHtml(shortId(latest.id))} · ${escapeHtml(formatDateTime(latest.finishedAt))}` : "Nenhuma pesquisa concluída neste dispositivo."}</p>
        </button>
        <button class="action-card sync" type="button" data-action="open-sync">
          <span class="action-icon">↻</span>
          <h3>Sincronização</h3>
          <p>${summary.pending ? `${summary.pending} item(ns) aguardando conexão ou nova tentativa.` : lastSyncedAt ? `Tudo enviado · última confirmação ${escapeHtml(formatDateTime(lastSyncedAt))}.` : "Envio automático ativo; nenhuma entrevista pendente."}</p>
        </button>
        <button class="action-card" type="button" data-action="delete-latest" ${latest ? "" : "disabled"}>
          <span class="action-icon">×</span>
          <h3>Solicitar exclusão</h3>
          <p>Solicita que a última entrevista seja desconsiderada, sem apagar o registro.</p>
        </button>
        <button class="action-card" type="button" data-action="export-backup">
          <span class="action-icon">↓</span>
          <h3>Exportar backup</h3>
          <p>Baixa uma cópia JSON das entrevistas e da fila deste dispositivo.</p>
        </button>
      </section>

      ${partials.length ? `<section class="panel"><div class="panel-heading"><h2>Entrevistas encerradas parcialmente</h2><p>Respostas preservadas neste aparelho e no backup JSON. Não são enviadas como entrevistas concluídas.</p></div>
        ${partials.map(item => `<div class="review-card"><strong>${escapeHtml(shortId(item.id))}</strong><p>Encerrada em ${escapeHtml(formatDateTime(item.abandonedAt))}</p><button class="button secondary small" type="button" data-action="resume-partial" data-id="${escapeHtml(item.id)}" ${draft ? "disabled" : ""}>Retomar parcial</button></div>`).join("")}
      </section>` : ""}
      <footer class="home-footer">
        <span>Experimento v${escapeHtml(state.experiment.experiment.version)} · Aplicativo v${escapeHtml(state.experiment.experiment.app_version)}</span>
        <span>${navigator.onLine ? "Online · envio automático a cada 30 s" : "Offline · dados preservados neste aparelho"}</span>
      </footer>
    </main>
  `, { title: "Experimento PD", subtitle: isSuperuser() ? "Coleta no dispositivo" : "Portal do pesquisador", adminReturn: isSuperuser(), logout: true });
}

function stepper(currentStep) {
  return `<nav class="stepper" aria-label="Etapas da entrevista">
    ${STEP_LABELS.map((label, index) => `<span class="step ${index === currentStep ? "current" : index < currentStep ? "complete" : ""}">${escapeHtml(label)}</span>`).join("")}
  </nav>`;
}

function draftSaveIndicator() {
  const saving = state.draftSaveState === "saving";
  const failed = state.draftSaveState === "error";
  return `<div class="draft-save-indicator ${saving ? "saving" : ""} ${failed ? "error" : ""}" id="draft-save-indicator" role="status" aria-live="polite">
    ${iconSvg(saving ? "refresh" : (failed ? "refresh" : "check"))}
    <span>${saving ? "Salvando…" : (failed ? "Não foi possível salvar" : "Salvo neste dispositivo")}</span>
  </div>`;
}

function surveyFrame(content, currentStep) {
  const interview = state.activeInterview;
  return appFrame(`
    <main class="page">
      <div class="survey-header">
        <div>
          <p class="eyebrow">Entrevista ${escapeHtml(shortId(interview.id))}</p>
          <div class="survey-meta">${escapeHtml(interview.designSnapshot.id)} · iniciada ${escapeHtml(formatDateTime(interview.startedAt))}</div>
        </div>
        <div class="survey-header-status">
          ${draftSaveIndicator()}
          ${statusBadge(interview.status)}
        </div>
      </div>
      ${stepper(currentStep)}
      ${content}
    </main>
  `, { title: "Experimento PD", subtitle: `Bloco ${interview.designSnapshot.id}`, survey: true });
}

function renderFieldContext() {
  const questionnaire = state.activeInterview.questionnaireSnapshot;
  const context = questionnaire.field_context;
  const answers = state.activeInterview.responses.field_context || {};
  const approachType = answers.approach_type || "";
  const historicalLine = answers.line_or_location && !BUS_LINES.some(line => line.value === answers.line_or_location);
  const stopAnswer = answers.pitaguari_serves_stop == null
    ? ""
    : (answers.pitaguari_serves_stop ? "sim" : "nao");

  return surveyFrame(`
    <section class="panel">
      <div class="panel-heading">
        <p class="eyebrow">Etapa 1 de 8</p>
        <h2>Contexto da abordagem</h2>
        <p>O pesquisador registra estas informações antes de iniciar as perguntas ao participante.</p>
      </div>
      ${state.formError ? `<p class="form-error" role="alert">${escapeHtml(state.formError)}</p>` : ""}
      <form id="field-context-form">
        <div class="form-field">
          <span class="field-label">${escapeHtml(context.approach_type.label)}</span>
          ${renderRadioOptions(context.approach_type, approachType)}
        </div>
        <div class="form-field" data-context-mode="embarcado" style="margin-top:1.25rem" ${approachType === "embarcado" ? "" : "hidden"}>
          ${historicalLine ? `<p class="field-help">Registro histórico: ${escapeHtml(answers.bus_line_label || answers.line_or_location)}</p>` : ""}
          <label for="${escapeHtml(context.bus_line.id)}">${escapeHtml(context.bus_line.label)}</label>
          <select id="${escapeHtml(context.bus_line.id)}" name="${escapeHtml(context.bus_line.id)}" ${approachType === "embarcado" ? "" : "disabled"} ${historicalLine ? "" : "required"}>
            <option value="">${historicalLine ? "Manter registro anterior" : "Selecione a linha"}</option>
            ${BUS_LINES.map(option => `<option value="${escapeHtml(option.value)}" ${answers.line_or_location === option.value ? "selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
          </select>
        </div>
        <div class="form-field" data-context-mode="ponto" style="margin-top:1.25rem" ${approachType === "ponto" ? "" : "hidden"}>
          <span class="field-label">${escapeHtml(context.pitaguari_serves_stop.label)}</span>
          ${renderRadioOptions(context.pitaguari_serves_stop, stopAnswer, 2, approachType !== "ponto")}
        </div>
        ${state.activeInterview.collectionVersion >= 2 ? `<fieldset class="form-field" style="margin-top:1rem"><legend id="duplicate-question-label">${escapeHtml(duplicateQuestion(approachType).label)}</legend>${renderRadioOptions(duplicateQuestion(approachType), state.activeInterview.responses.recent_participation)}</fieldset>` : ""}
        <p class="field-help" style="margin-top:1rem">O grupo tratado ou controle será definido automaticamente a partir desta informação.</p>
        <div class="form-actions">
          <span></span>
          <button class="button" type="submit">Iniciar entrevista →</button>
        </div>
      </form>
    </section>
  `, 0);
}

function renderProfile() {
  const questionnaire = state.activeInterview.questionnaireSnapshot;
  const answers = state.activeInterview.responses.profile || {};
  const gender = questionnaire.profile.gender;
  const age = questionnaire.profile.age;
  const mobility = state.activeInterview.responses.mobility || {};
  const habit = questionnaire.mobility.metro_habit;
  const neighborhood = questionnaire.mobility.neighborhood;

  return surveyFrame(`
    <section class="panel">
      <div class="panel-heading">
        <p class="eyebrow">Etapa 2 de 8</p>
        <h2>Perfil da pessoa entrevistada</h2>
        <p>Registre o perfil e os hábitos de deslocamento. Os campos desta entrevista são obrigatórios.</p>
      </div>
      <div class="form-guidance">${iconSvg("check")}<p>As respostas são salvas automaticamente neste dispositivo. Você pode voltar sem perder o que já preencheu.</p></div>
      ${state.formError ? `<p class="form-error" role="alert">${escapeHtml(state.formError)}</p>` : ""}
      <form id="profile-form">
        <div class="form-field">
          <span class="field-label">${escapeHtml(gender.label)}</span>
          ${renderRadioOptions(gender, answers.gender_identity)}
        </div>
        <div class="form-field" style="margin-top:1.25rem">
          <label for="${escapeHtml(age.id)}">${escapeHtml(age.label)}</label>
          <input id="${escapeHtml(age.id)}" name="${escapeHtml(age.id)}" type="number" inputmode="numeric" min="${age.min}" max="${age.max}" value="${answers.age_years ?? ""}" required>
        </div>

        <div class="form-field">
          <span class="field-label">${escapeHtml(habit.label)}</span>
          ${renderRadioOptions(habit, mobility.metro_habit)}
        </div>
        <div class="form-field" style="margin-top:1.25rem">
          <label for="${escapeHtml(neighborhood.id)}">${escapeHtml(neighborhood.label)}</label>
          <div class="search-control">
            ${iconSvg("search")}
            <input id="neighborhood-search" type="search" inputmode="search" autocomplete="off" placeholder="Digite o nome do bairro" aria-controls="${escapeHtml(neighborhood.id)} neighborhood-results-status">
          </div>
          <select id="${escapeHtml(neighborhood.id)}" name="${escapeHtml(neighborhood.id)}" required>
            ${renderNeighborhoodOptions(state.neighborhoodCatalog, mobility)}
          </select>
          <div id="other-neighborhood-field" ${mobility.origin_neighborhood_category === "outro" ? "" : "hidden"}>
            <label for="origin_neighborhood_other">Nome do bairro</label>
            <input id="origin_neighborhood_other" name="origin_neighborhood_other" maxlength="${neighborhood.max_length}" value="${escapeHtml(mobility.origin_neighborhood_other || "")}" ${mobility.origin_neighborhood_category === "outro" ? "required" : "disabled"}>
            <label for="origin_city_other">Município</label>
            <input id="origin_city_other" name="origin_city_other" maxlength="120" value="${escapeHtml(mobility.origin_city || "")}" ${mobility.origin_neighborhood_category === "outro" ? "required" : "disabled"}>
          </div>
          <p class="field-help" id="neighborhood-results-status" aria-live="polite">${filterNeighborhoodEntries(state.neighborhoodCatalog).length} bairros disponíveis · Maracanaú primeiro · lista offline v${escapeHtml(state.neighborhoodCatalog.catalog_version)}.</p>
        </div>
        <div class="form-actions">
          <button class="button secondary" type="button" data-action="back-to-context">← Contexto</button>
          <button class="button" type="submit">Explicar os cenários →</button>
        </div>
      </form>
    </section>
  `, 1);
}

function renderScenarioIntro() {
  const briefing = state.activeInterview.experimentQuestionnaireSnapshot?.scenario_briefing || state.experiment.questionnaire.scenario_briefing;
  return surveyFrame(`<section class="panel">
    <div class="panel-heading"><p class="eyebrow">Antes dos cenários</p><h2>Como responder às próximas escolhas</h2><p>Orientação para o entrevistador: leia ou explique este resumo ao participante.</p></div>
    <div class="scenario-briefing">${briefing.paragraphs.map(text => `<p>${escapeHtml(text.replace(/transbordo/gi, "integração"))}</p>`).join("")}</div>
    <div class="form-actions"><button class="button secondary" type="button" data-action="intro-back">← Perfil</button><button class="button" type="button" data-action="start-scenarios">Explicação feita · iniciar cenários →</button></div>
  </section>`, 2);
}

function renderScenario() {
  const interview = state.activeInterview;
  const tasks = interview.designSnapshot.tasks;
  const task = tasks[state.choiceIndex];
  const savedChoice = interview.responses.choices?.[task.task_id];
  const progress = ((state.choiceIndex + 1) / tasks.length) * 100;
  const experimentQuestionnaire = interview.experimentQuestionnaireSnapshot || state.experiment.questionnaire;

  const alternativeCards = renderScenarioCards(task, savedChoice?.selected_alternative, iconSvg);

  return surveyFrame(`
    <section class="panel">
      <div class="scenario-progress">
        <strong>Cenário ${state.choiceIndex + 1} de ${tasks.length}</strong>
        <div class="progress-track" aria-hidden="true"><div class="progress-fill" style="width:${progress}%"></div></div>
      </div>
      <div class="scenario-intro">
        <strong>${escapeHtml(experimentQuestionnaire.introduction)}</strong><br>
        ${escapeHtml(experimentQuestionnaire.choice_prompt)}
      </div>
      <div class="scenario-options">${alternativeCards}</div>
      ${state.formError ? `<p class="form-error" role="alert">${escapeHtml(state.formError)}</p>` : ""}
      <div class="form-actions">
        <button class="button secondary" type="button" data-action="previous-scenario">← Voltar</button>
        <button class="button" type="button" data-action="next-scenario" ${savedChoice ? "" : "disabled"}>${state.choiceIndex === tasks.length - 1 ? "Continuar →" : "Próximo cenário →"}</button>
      </div>
    </section>
  `, 3);
}

function renderMobility() { return renderProfile(); }

function renderPsychometrics(kind) {
  const questionnaire = state.activeInterview.questionnaireSnapshot;
  const psychometrics = questionnaire.psychometrics;
  const section = psychometrics[kind];
  const answers = state.activeInterview.responses.psychometrics?.[kind] || {};
  const kindIndex = PSYCHOMETRIC_KINDS.indexOf(kind);
  const answeredCount = section.items.filter(item => answers[item.id]).length;
  const totalCount = section.items.length;
  const completion = totalCount ? (answeredCount / totalCount) * 100 : 0;

  return surveyFrame(`
    <section class="panel">
      <div class="panel-heading">
        <p class="eyebrow">Etapa ${kindIndex + 5} de 8</p>
        <h2>${escapeHtml(section.title)}</h2>
        <p>Leia cada frase e marque uma opção. Todas precisam ser respondidas.</p>
        ${section.introduction ? `<p>${escapeHtml(section.introduction)}</p>` : ""}
      </div>
      <div class="section-progress" role="status" aria-live="polite">
        <div><strong id="psychometric-count">${answeredCount} de ${totalCount} respondidas</strong><span>O aplicativo mostrará qualquer item que faltar.</span></div>
        <div class="progress-track" aria-hidden="true"><div class="progress-fill" id="psychometric-progress" style="width:${completion}%"></div></div>
      </div>
      ${state.formError ? `<p class="form-error" role="alert">${escapeHtml(state.formError)}</p>` : ""}
      <form id="psychometric-form" data-kind="${kind}">
        ${renderLikertSection(section, psychometrics.scale, answers)}
        <div class="form-actions">
          <button class="button secondary" type="button" data-action="psychometric-back" data-kind="${kind}">← Voltar</button>
          <button class="button" type="submit">${kindIndex < PSYCHOMETRIC_KINDS.length - 1 ? `${escapeHtml(questionnaire.psychometrics[PSYCHOMETRIC_KINDS[kindIndex + 1]].title)} →` : "Revisar entrevista →"}</button>
        </div>
      </form>
    </section>
  `, kindIndex + 4);
}

function renderReview() {
  const interview = state.activeInterview;
  const questionnaire = interview.questionnaireSnapshot;
  const profile = interview.responses.profile;
  const mobility = interview.responses.mobility;
  const fieldContext = interview.responses.field_context;
  const choices = interview.designSnapshot.tasks.map((task, index) => {
    const answer = interview.responses.choices[task.task_id];
    const alternative = task.alternatives.find(item => item.id === answer?.selected_alternative);
    return `Cenário ${index + 1}: ${escapeHtml(alternative?.label || "Não respondido")}`;
  });

  return surveyFrame(`
    <section class="panel">
      <div class="panel-heading">
        <p class="eyebrow">Etapa 8 de 8</p>
        <h2>Revisar e concluir</h2>
        <p>Confira o resumo antes de finalizar. Ainda é possível voltar e alterar respostas.</p>
      </div>
      ${state.formError ? `<p class="form-error" role="alert">${escapeHtml(state.formError)}</p>` : ""}
      <div class="review-grid">
        <article class="review-card">
          <h3>Contexto <span class="review-complete">${iconSvg("check")} Completo</span></h3>
          <ul class="review-list">
            <li>Abordagem: ${escapeHtml(optionLabel(questionnaire.field_context.approach_type, fieldContext.approach_type))}</li>
            ${fieldContext.approach_type === "embarcado"
              ? `<li>Linha: ${escapeHtml(fieldContext.bus_line_label || optionLabel(questionnaire.field_context.bus_line, fieldContext.line_or_location))}</li>`
              : `<li>Pitaguary passa na parada: ${fieldContext.pitaguari_serves_stop ? "Sim" : "Não"}</li>`}
            <li>Grupo calculado: ${escapeHtml(optionLabel(questionnaire.field_context.treatment_group, fieldContext.treatment_group))}</li>
          </ul>
          <button class="button secondary small" type="button" data-action="review-edit-context">Editar contexto</button>
        </article>
        <article class="review-card">
          <h3>Perfil <span class="review-complete">${iconSvg("check")} Completo</span></h3>
          <ul class="review-list">
            <li>Gênero: ${escapeHtml(optionLabel(questionnaire.profile.gender, profile.gender_identity))}</li>
            <li>Idade: ${escapeHtml(profile.age_years)} anos</li>
          </ul>
          <button class="button secondary small" type="button" data-action="review-edit-profile">Editar perfil</button>
        </article>
        <article class="review-card">
          <h3>Hábitos <span class="review-complete">${iconSvg("check")} Completo</span></h3>
          <ul class="review-list">
            <li>Metrô: ${escapeHtml(optionLabel(questionnaire.mobility.metro_habit, mobility.metro_habit))}</li>
            <li>Município: ${escapeHtml(mobility.origin_city)}</li>
            <li>Bairro: ${escapeHtml(mobility.origin_neighborhood)}</li>
          </ul>
          <button class="button secondary small" type="button" data-action="review-edit-mobility">Editar hábitos</button>
        </article>
        <article class="review-card">
          <h3>Escolhas <span class="review-complete">${iconSvg("check")} Completo</span></h3>
          <ul class="review-list"><li>${choices.join("</li><li>")}</li></ul>
          <button class="button secondary small" type="button" data-action="review-edit-choices">Editar cenários</button>
        </article>
        <article class="review-card">
          <h3>Segurança <span class="review-complete">${iconSvg("check")} Completo</span></h3>
          <p>${Object.keys(interview.responses.psychometrics.security || {}).length} de ${questionnaire.psychometrics.security.items.length} respostas registradas.</p>
          <button class="button secondary small" type="button" data-action="review-edit-security">Editar segurança</button>
        </article>
        <article class="review-card">
          <h3>Conforto <span class="review-complete">${iconSvg("check")} Completo</span></h3>
          <p>${Object.keys(interview.responses.psychometrics.comfort || {}).length} de ${questionnaire.psychometrics.comfort.items.length} respostas registradas.</p>
          <button class="button secondary small" type="button" data-action="review-edit-comfort">Editar conforto</button>
        </article>
        <article class="review-card">
          <h3>${escapeHtml(questionnaire.psychometrics.trust.title)} <span class="review-complete">${iconSvg("check")} Completo</span></h3>
          <p>${Object.keys(interview.responses.psychometrics.trust || {}).length} de ${questionnaire.psychometrics.trust.items.length} respostas registradas.</p>
          <button class="button secondary small" type="button" data-action="review-edit-trust">Editar confiança</button>
        </article>
      </div>
      <form id="review-flags-form"><label class="test-interview-option"><input type="checkbox" name="is_test" ${interview.is_test === true ? "checked" : ""}> Marcar esta entrevista como teste</label></form>
      <div class="callout"><p>Ao concluir, a entrevista será salva neste dispositivo e enviada automaticamente ao banco quando houver internet.</p></div>
      <div class="form-actions">
        <button class="button secondary" type="button" data-action="back-to-trust">← Confiança</button>
        <button class="button success" type="button" data-action="finish-interview" ${state.busy ? "disabled" : ""}>✓ Concluir entrevista</button>
      </div>
    </section>
  `, 7);
}

function renderComplete() {
  const synchronized = state.syncResult?.success > 0 && !state.syncResult?.errors;
  return appFrame(`
    <main class="page">
      <section class="panel sync-panel">
        <div class="brand-mark" style="margin-inline:auto" aria-hidden="true">✓</div>
        <p class="eyebrow">Entrevista salva</p>
        <h1>Pesquisa concluída</h1>
        <p>A entrevista foi armazenada com segurança neste dispositivo e continuará disponível mesmo sem internet.</p>
        ${synchronized
          ? `<div class="callout success"><p>Envio confirmado pelo banco. Você já pode iniciar a próxima entrevista.</p></div>`
          : `<div class="callout warning"><p>O envio automático continuará em segundo plano e será repetido quando houver conexão.</p></div>`}
        <div class="form-actions">
          <button class="button secondary" type="button" data-action="go-home">Voltar ao início</button>
          ${synchronized ? `<button class="button" type="button" data-action="new-interview">Iniciar próxima pesquisa</button>` : `<button class="button" type="button" data-action="open-sync">Ver sincronização</button>`}
        </div>
      </section>
    </main>
  `, { title: "Experimento PD", subtitle: "Entrevista concluída", logout: true });
}

async function renderDeletion() {
  const latest = await getLatestCompletedInterview();
  if (!latest) {
    queueMicrotask(() => navigate("researcher-home"));
    return "";
  }
  return appFrame(`
    <main class="page">
      <section class="panel">
        <div class="panel-heading">
          <p class="eyebrow">Exclusão lógica</p>
          <h2>Solicitar exclusão da última pesquisa</h2>
          <p>Entrevista ${escapeHtml(shortId(latest.id))}, concluída ${escapeHtml(formatDateTime(latest.finishedAt))}.</p>
        </div>
        <div class="callout warning"><p>A entrevista não será apagada. O banco preservará o pedido, o motivo e o registro original para auditoria.</p></div>
        ${state.formError ? `<p class="form-error" role="alert">${escapeHtml(state.formError)}</p>` : ""}
        <form id="delete-form" data-id="${escapeHtml(latest.id)}">
          <div class="form-field">
            <label for="delete-reason">Motivo da exclusão</label>
            <textarea id="delete-reason" name="reason" maxlength="500" required placeholder="Descreva por que esta entrevista deve ser desconsiderada."></textarea>
          </div>
          <div class="form-actions">
            <button class="button secondary" type="button" data-action="go-home">Cancelar</button>
            <button class="button danger" type="submit">Confirmar solicitação</button>
          </div>
        </form>
      </section>
    </main>
  `, { title: "Experimento PD", subtitle: "Solicitar exclusão", logout: true });
}

async function renderSync() {
  const summary = await dashboardSummary();
  const progress = state.syncProgress;
  const percent = progress?.total ? Math.round((progress.current / progress.total) * 100) : 0;

  return appFrame(`
    <main class="page">
      <section class="panel sync-panel">
        <p class="eyebrow">Sincronização</p>
        <h1>Enviar pesquisas</h1>
        <span class="sync-number">${summary.pending}</span>
        <p>${summary.pending === 1 ? "item aguarda envio" : "itens aguardam envio"}</p>

        ${state.session?.offline ? `<div class="callout warning"><p>Esta sessão foi restaurada no modo offline. Reconecte o aparelho para validar a conta e enviar os dados ao banco.</p></div>` : ""}
        ${!navigator.onLine ? `<div class="callout warning"><p>O aparelho está offline. As entrevistas permanecem salvas e poderão ser enviadas quando a conexão voltar.</p></div>` : ""}
        ${state.formError ? `<p class="form-error" role="alert">${escapeHtml(state.formError)}</p>` : ""}

        ${progress ? `
          <div class="progress-track" aria-label="Progresso da sincronização"><div class="progress-fill" style="width:${percent}%"></div></div>
          <p>${progress.state === "complete"
            ? "Sincronização concluída."
            : progress.state === "retrying"
              ? `A conexão oscilou. Nova tentativa ${progress.retry.attempt} de ${progress.retry.maximumAttempts}…`
              : `Processando ${progress.current} de ${progress.total}…`}</p>
        ` : ""}

        ${state.syncResult ? `<div class="callout ${state.syncResult.errors ? "warning" : "success"}"><p>${state.syncResult.success} enviado(s); ${state.syncResult.errors} erro(s).</p></div>` : ""}

        <div class="sync-list">
          ${summary.queue.length ? summary.queue.map(item => `
            <div class="sync-item">
              <span><strong>${item.action === "request_deletion" ? "Exclusão" : "Entrevista"}</strong> <code>${escapeHtml(shortId(item.interviewId))}</code></span>
              <span>${statusBadge(item.status)}${item.lastError ? `<small class="danger-text">${escapeHtml(item.lastError)}</small>` : ""}</span>
            </div>
          `).join("") : `<div class="empty-state">Não há itens pendentes.</div>`}
        </div>

        <div class="form-actions">
          <button class="button secondary" type="button" data-action="go-home">← Voltar</button>
          <button class="button" type="button" data-action="run-sync" ${state.busy || !summary.pending || !navigator.onLine || state.session?.offline ? "disabled" : ""}>${state.busy ? "Sincronizando…" : "Sincronizar agora"}</button>
        </div>
      </section>
    </main>
  `, { title: "Experimento PD", subtitle: "Sincronização", logout: true });
}

const ADMIN_PHASES = Object.freeze([
  { id: "all", label: "Todas" },
  { id: "pd_antes", label: "PD antes" },
  { id: "pd_durante", label: "PD durante" },
  { id: "pd_depois", label: "PD depois" }
]);

const DASHBOARD_LABELS = Object.freeze({
  mulher: "Mulher",
  homem: "Homem",
  nao_binario_outro: "Não binário ou outra identidade",
  prefiro_nao_responder: "Prefiro não responder",
  regularmente: "Utiliza regularmente",
  muito_raramente: "Utiliza muito raramente",
  ja_utilizei_nao_costumo: "Já utilizou, mas não costuma",
  nunca_utilizei: "Nunca utilizou",
  bus: "Continuar no ônibus",
  metro: "Ônibus + metrô",
  tie: "Sem predominância"
});

function dashboardLabel(value) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short" }).format(new Date(`${value}T12:00:00`));
  }
  return DASHBOARD_LABELS[value] || value;
}

function dashboardDistribution(title, items = [], total = 0, limit = 10, className = "") {
  const visible = items.filter(item => item.count > 0).slice(0, limit);
  const maximum = Math.max(1, ...visible.map(item => item.count));
  return `
    <article class="dashboard-chart ${className}">
      <h3>${escapeHtml(title)}</h3>
      ${visible.length ? `<div class="bar-list">${visible.map(item => {
        const percentage = total ? Math.round((item.count / total) * 100) : 0;
        const label = dashboardLabel(item.value);
        return `<div class="bar-row">
          <div class="bar-label"><span title="${escapeHtml(label)}">${escapeHtml(label)}</span><strong>${item.count}${total ? ` · ${percentage}%` : ""}</strong></div>
          <div class="bar-track" aria-hidden="true"><span style="width:${Math.round((item.count / maximum) * 100)}%"></span></div>
        </div>`;
      }).join("")}</div>` : `<p class="empty-state compact">Ainda não há respostas nesta categoria.</p>`}
    </article>`;
}

const ANALYTICS_GENDERS = Object.freeze([
  { id: "all", label: "Todos os gêneros" },
  { id: "mulher", label: "Mulheres" },
  { id: "homem", label: "Homens" },
  { id: "nao_binario_outro", label: "Outras identidades" },
  { id: "prefiro_nao_responder", label: "Prefere não responder" }
]);
const ANALYTICS_TREATMENTS = Object.freeze([
  { id: "all", label: "Todos os grupos" },
  { id: "pitaguari", label: "Pitaguari" },
  { id: "controle", label: "Controle" }
]);

function formatScore(value) {
  return value == null
    ? "—"
    : new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 }).format(value);
}

function analyticsFilter(analytics) {
  const activeVersion = state.adminAnalyticsVersion || state.questionnaire.questionnaire_version;
  const versions = [...new Set([state.questionnaire.questionnaire_version, ...(state.adminDocuments || []).map(item => Number(item.questionnaire_version)).filter(Boolean)])].sort((a, b) => b - a);
  return `<section class="analytics-filter" aria-label="Filtros das análises de escolha">
    <div><strong>Versão do questionário</strong><span>As escalas são analisadas separadamente por versão.</span></div>
    <div class="filter-chips">${versions.map(version => `<button class="filter-chip ${version === activeVersion ? "active" : ""}" type="button" data-action="select-analytics-version" data-version="${version}" aria-pressed="${version === activeVersion}">Versão ${version}</button>`).join("")}</div>
    <div><strong>Comparar escolhas por perfil e grupo</strong><span>${analytics.interviews} entrevista(s) · ${analytics.decisions} decisão(ões) de cenário</span></div>
    <div class="filter-chips">
      ${ANALYTICS_GENDERS.map(option => `<button class="filter-chip ${state.adminAnalyticsGender === option.id ? "active" : ""}" type="button" data-action="select-analytics-gender" data-gender="${option.id}" aria-pressed="${state.adminAnalyticsGender === option.id}">${escapeHtml(option.label)}</button>`).join("")}
      ${ANALYTICS_TREATMENTS.map(option => `<button class="filter-chip ${state.adminAnalyticsTreatment === option.id ? "active" : ""}" type="button" data-action="select-analytics-treatment" data-treatment="${option.id}" aria-pressed="${state.adminAnalyticsTreatment === option.id}">${escapeHtml(option.label)}</button>`).join("")}
    </div>
  </section>`;
}

function choiceByGenderChart(items = []) {
  return `<article class="dashboard-chart">
    <h3>Escolha por gênero</h3>
    <p class="chart-description">Percentual das decisões de cenário em cada alternativa.</p>
    <div class="choice-legend"><span class="bus">Continuar no ônibus</span><span class="metro">Ônibus + metrô</span></div>
    ${items.length ? `<div class="choice-stack-list">${items.map(item => {
      const busShare = item.total ? Math.round((item.bus / item.total) * 100) : 0;
      const metroShare = item.total ? 100 - busShare : 0;
      return `<div class="choice-stack-row">
        <div class="bar-label"><span>${escapeHtml(dashboardLabel(item.value))}</span><strong>${item.total} decisões</strong></div>
        <div class="choice-stack" aria-label="${escapeHtml(dashboardLabel(item.value))}: ${busShare}% ônibus e ${metroShare}% ônibus mais metrô">
          <span class="bus" style="width:${busShare}%">${busShare >= 14 ? `${busShare}%` : ""}</span>
          <span class="metro" style="width:${metroShare}%">${metroShare >= 14 ? `${metroShare}%` : ""}</span>
        </div>
      </div>`;
    }).join("")}</div>` : `<p class="empty-state compact">Ainda não há decisões para este filtro.</p>`}
  </article>`;
}

function psychometricCard(kind, section) {
  const titles = {
    security: "Percepção de segurança",
    comfort: "Percepção de conforto",
    trust: "Confiança no metrô"
  };
  const safeSection = section || { mean: null, items: [] };
  return `<article class="dashboard-chart psychometric-chart">
    <div class="chart-title-with-score"><div><h3>${escapeHtml(safeSection.title || titles[kind] || kind)}</h3><p class="chart-description">Média ajustada de 1 a 5.</p></div><strong>${formatScore(safeSection.mean)}</strong></div>
    ${safeSection.items.length ? `<div class="score-list">${safeSection.items.map(item => `
      <div class="score-row" title="${escapeHtml(item.text)}">
        <div class="score-label"><strong>${escapeHtml(item.id)}</strong><span>${escapeHtml(item.text)}</span><b>${formatScore(item.adjusted_mean)}</b></div>
        <div class="score-track" aria-hidden="true"><span style="width:${Math.max(0, Math.min(100, Number(item.adjusted_mean || 0) * 20))}%"></span></div>
        ${item.reverse_scored ? `<small>Pontuação invertida para compor a escala</small>` : ""}
      </div>
    `).join("")}</div>` : `<p class="empty-state compact">Ainda não há respostas psicométricas.</p>`}
  </article>`;
}

function psychometricChoiceProfile(items = []) {
  return `<article class="dashboard-chart">
    <h3>Perfil psicométrico e escolha predominante</h3>
    <p class="chart-description">Médias ajustadas agrupadas pela alternativa escolhida na maioria dos quatro cenários.</p>
    ${items.length ? `<div class="psychometric-profile-list">${items.map(item => `
      <div class="psychometric-profile-row">
        <div><strong>${escapeHtml(dashboardLabel(item.value))}</strong><span>${item.interviews} entrevista(s)</span></div>
        <dl><div><dt>Segurança</dt><dd>${formatScore(item.security_mean)}</dd></div><div><dt>Conforto</dt><dd>${formatScore(item.comfort_mean)}</dd></div><div><dt>Confiança</dt><dd>${formatScore(item.trust_mean)}</dd></div></dl>
      </div>
    `).join("")}</div>` : `<p class="empty-state compact">Ainda não há respostas suficientes para o cruzamento.</p>`}
  </article>`;
}

function attributeInfluence(attributes = []) {
  return `<section class="panel admin-section attribute-analysis">
    <div class="panel-heading"><p class="eyebrow">Experimento de escolha</p><h2>Influência descritiva dos atributos</h2><p>Para cada nível apresentado, veja a proporção de vezes em que a alternativa ônibus + metrô foi escolhida. Isso permite observar tendências de tempo, caminhada, iluminação, escadas, lotação e informação na estação.</p></div>
    ${attributes.length ? `<div class="attribute-analysis-grid">${attributes.map(attribute => `
      <article class="attribute-analysis-card">
        <div><h3>${escapeHtml(attribute.label)}</h3><span>${attribute.unit ? `Unidade: ${escapeHtml(attribute.unit)}` : "Condição apresentada"}</span></div>
        <div class="attribute-level-list">${attribute.levels.map(level => `
          <div class="attribute-level">
            <div class="bar-label"><span>${escapeHtml(level.display_value)}</span><strong>${formatScore(level.metro_share)}%</strong></div>
            <div class="score-track" aria-hidden="true"><span style="width:${level.metro_share}%"></span></div>
            <small>${level.metro} de ${level.count} decisões escolheram ônibus + metrô</small>
          </div>
        `).join("")}</div>
      </article>
    `).join("")}</div>` : `<p class="empty-state">Ainda não há escolhas sincronizadas para este filtro.</p>`}
    <div class="callout neutral"><p>Estes percentuais são descritivos. A estimação do modelo de preferência declarada continuará sendo feita na análise estatística, considerando os atributos simultaneamente.</p></div>
  </section>`;
}

function adminReport() {
  return state.adminPhase === "all"
    ? state.adminDashboard?.overall
    : state.adminDashboard?.by_phase?.[state.adminPhase];
}

function phaseTabs() {
  return `<div class="phase-tabs" role="tablist" aria-label="Etapa da pesquisa">
    ${ADMIN_PHASES.map(phase => `<button class="phase-tab ${state.adminPhase === phase.id ? "active" : ""}" type="button" role="tab" aria-selected="${state.adminPhase === phase.id}" data-action="select-admin-phase" data-phase="${phase.id}">${phase.label}</button>`).join("")}
  </div>`;
}

function adminDataState() {
  if (state.adminDashboardLoading && !state.adminDashboard) return `<div class="dashboard-loading"><span class="spinner" aria-hidden="true"></span><p>Atualizando os dados da coleta…</p></div>`;
  if (state.adminDashboardError && !state.adminDashboard) return `<div class="callout error"><p>${escapeHtml(state.adminDashboardError)}</p></div>`;
  if (!state.adminDashboard) return `<div class="dashboard-empty"><p>Não foi possível carregar os dados centrais.</p><button class="button small" type="button" data-action="refresh-admin-dashboard">Tentar novamente</button></div>`;
  return "";
}

function centralStatus(report) {
  return `<div class="central-status">
    <span class="live-dot"></span>
    <div><strong>Banco atualizado</strong><span>${state.adminRealtimeActive ? "Atualização em tempo real ativa" : "Verificação automática a cada 60 segundos"}. Último recebimento: ${report?.latest_received_at ? escapeHtml(formatDateTime(report.latest_received_at)) : "nenhum registro"}.</span></div>
  </div>`;
}

function researcherTable(report, limit = null) {
  const researchers = limit ? (report?.researchers || []).slice(0, limit) : (report?.researchers || []);
  return `<div class="table-wrap"><table class="admin-table">
    <thead><tr><th>Pesquisador</th><th>Sincronizadas</th><th>Completas</th><th>Válidas</th><th>Exclusões</th><th>Último envio</th></tr></thead>
    <tbody>${researchers.length ? researchers.map(researcher => `<tr>
      <td><div class="table-person"><strong>${escapeHtml(researcher.name)}</strong><span>${escapeHtml(researcher.email)}</span></div></td>
      <td><strong>${researcher.synchronized}</strong></td><td>${researcher.complete}</td><td>${researcher.valid}</td>
      <td>${researcher.deletion_requests ? `<span class="danger-text">${researcher.deletion_requests}</span>` : "0"}</td>
      <td>${researcher.latest_at ? escapeHtml(formatDateTime(researcher.latest_at)) : "—"}</td>
    </tr>`).join("") : `<tr><td colspan="6" class="empty-state">Nenhum pesquisador ou envio encontrado.</td></tr>`}</tbody>
  </table></div>`;
}

function accountRoleLabel(role) {
  return ({ superuser: "Superusuário", admin: "Administrador", researcher: "Pesquisador" })[role] || "Sem perfil";
}

function renderAdminOverview() {
  const stateBlock = adminDataState();
  if (stateBlock) return stateBlock;
  const report = adminReport();
  const totals = report.totals;
  const qualityIssues = Object.values(report.data_quality || {}).reduce((sum, value) => sum + Number(value || 0), 0);
  return `
    ${phaseTabs()}
    ${centralStatus(report)}
    <section class="metrics-grid admin-metrics" aria-label="Resumo da coleta">
      <div class="metric-card"><span class="metric-label">Pesquisas sincronizadas</span><strong class="metric-value">${totals.synchronized}</strong><span class="metric-note">Recebidas de todos os dispositivos</span></div>
      <div class="metric-card"><span class="metric-label">Pesquisas completas</span><strong class="metric-value">${totals.complete}</strong><span class="metric-note">Questionários integralmente preenchidos</span></div>
      <div class="metric-card"><span class="metric-label">Pesquisas válidas</span><strong class="metric-value">${totals.valid}</strong><span class="metric-note">Sem solicitação de exclusão</span></div>
      <div class="metric-card ${totals.deletion_requests ? "danger" : ""}"><span class="metric-label">Exclusões solicitadas</span><strong class="metric-value">${totals.deletion_requests}</strong><span class="metric-note">Registros preservados para auditoria</span></div>
    </section>
    <div class="overview-grid">
      ${dashboardDistribution("Pesquisas válidas por dia", report.distributions.daily, 0, 14, "daily-chart")}
      <article class="insight-card"><span class="insight-icon">${iconSvg("chart")}</span><div><span>Idade média</span><strong>${report.age_summary.mean ?? "—"}</strong><small>${report.age_summary.answered ? `${report.age_summary.min} a ${report.age_summary.max} anos · ${report.age_summary.answered} respostas` : "Sem respostas válidas"}</small></div></article>
      <article class="insight-card ${qualityIssues ? "warning" : ""}"><span class="insight-icon">${qualityIssues ? "!" : "✓"}</span><div><span>Qualidade dos dados</span><strong>${qualityIssues}</strong><small>${qualityIssues ? "pontos para revisão" : "nenhuma pendência identificada"}</small></div></article>
    </div>
    <section class="panel admin-section">
      <div class="panel-heading inline-heading"><div><p class="eyebrow">Equipe em campo</p><h2>Produção por pesquisador</h2><p>Dados consolidados de todos os dispositivos que já sincronizaram.</p></div><button class="button secondary small" type="button" data-action="admin-navigate" data-route="admin-users">${isSuperuser() ? "Gerenciar acessos" : "Ver pesquisadores"}</button></div>
      ${researcherTable(report)}
    </section>`;
}

function renderManagedUserCard(user) {
  const editable = isSuperuser() && user.role !== "superuser";
  if (editable && state.editingUserId === user.id) {
    return `<article class="researcher-card editing">
      <form class="managed-user-edit-form form-grid" data-user-id="${escapeHtml(user.id)}">
        <p class="form-error form-inline-error full" role="alert" hidden></p>
        <div class="form-field"><label for="edit-name-${escapeHtml(user.id)}">Nome completo</label><input id="edit-name-${escapeHtml(user.id)}" name="name" type="text" maxlength="128" value="${escapeHtml(user.name || "")}" required></div>
        <div class="form-field"><label for="edit-email-${escapeHtml(user.id)}">E-mail</label><input id="edit-email-${escapeHtml(user.id)}" name="email" type="email" value="${escapeHtml(user.email)}" required></div>
        <div class="form-field"><label for="edit-role-${escapeHtml(user.id)}">Perfil de acesso</label><select id="edit-role-${escapeHtml(user.id)}" name="role" required><option value="researcher" ${user.role === "researcher" ? "selected" : ""}>Pesquisador</option><option value="admin" ${user.role === "admin" ? "selected" : ""}>Administrador</option></select></div>
        <div class="form-actions compact-actions"><button class="button secondary small" type="button" data-action="cancel-edit-user">Cancelar</button><button class="button small" type="submit">Salvar alterações</button></div>
      </form>
    </article>`;
  }

  return `<article class="researcher-card">
    <div class="researcher-identity"><div><strong>${escapeHtml(user.name || "Sem nome")}</strong><span class="role-badge ${escapeHtml(user.role)}">${escapeHtml(accountRoleLabel(user.role))}</span><span class="account-status ${user.status ? "active" : ""}">${user.status ? "Ativo" : "Inativo"}</span></div><span>${escapeHtml(user.email)}</span><small>${user.mustChangePassword ? "Aguardando troca da senha temporária" : "Senha pessoal configurada"}</small></div>
    ${editable ? `<div class="researcher-actions">
      <button class="button secondary small" type="button" data-action="edit-managed-user" data-user-id="${escapeHtml(user.id)}">Editar</button>
      <form class="researcher-reset-form" data-user-id="${escapeHtml(user.id)}"><label class="sr-only" for="reset-${escapeHtml(user.id)}">Nova senha temporária</label><div class="password-control compact"><input id="reset-${escapeHtml(user.id)}" name="password" type="password" placeholder="Nova senha temporária" autocomplete="new-password" data-password-policy="true" required><button class="password-toggle" type="button" data-action="toggle-password" data-target="reset-${escapeHtml(user.id)}" aria-label="Mostrar senha" aria-pressed="false">${iconSvg("eye")}<span class="sr-only">Mostrar</span></button></div><button class="button secondary small" type="submit">Redefinir</button></form>
      <button class="button ${user.status ? "danger" : "secondary"} small" type="button" data-action="toggle-researcher" data-user-id="${escapeHtml(user.id)}" data-status="${user.status ? "false" : "true"}">${user.status ? "Desativar" : "Reativar"}</button>
    </div>` : ""}
  </article>`;
}

function renderCreateUserModal() {
  if (!isSuperuser() || !state.createUserModalOpen) return "";
  return `<div class="modal-backdrop" data-modal-backdrop>
    <section class="modal-card" role="dialog" aria-modal="true" aria-labelledby="create-user-title" aria-describedby="create-user-description">
      <header class="modal-header">
        <div><p class="eyebrow">Novo acesso</p><h2 id="create-user-title">Criar usuário</h2></div>
        <button class="modal-close" type="button" data-action="close-create-user" aria-label="Fechar">×</button>
      </header>
      <p id="create-user-description" class="modal-description">Crie um administrador de consulta ou um pesquisador de campo.</p>
      ${state.researchersError ? `<div class="callout error"><p>${escapeHtml(state.researchersError)}</p></div>` : ""}
      <form id="managed-user-create-form" class="form-grid researcher-create-form">
        <p class="form-error form-inline-error full" role="alert" hidden></p>
        <div class="form-field full"><label for="managed-user-role">Perfil de acesso</label><select id="managed-user-role" name="role" required><option value="researcher">Pesquisador</option><option value="admin">Administrador</option></select><p class="field-help">Administradores acompanham e exportam dados; pesquisadores executam entrevistas.</p></div>
        <div class="form-field full"><label for="managed-user-name">Nome completo</label><input id="managed-user-name" name="name" type="text" maxlength="128" required></div>
        <div class="form-field full"><label for="managed-user-email">E-mail</label><input id="managed-user-email" name="email" type="email" autocomplete="off" required></div>
        ${passwordField({ id: "managed-user-password", name: "password", label: "Senha temporária" })}
        <div class="form-field full">${passwordRequirementHint()}</div>
        <div class="callout neutral full"><p>A troca da senha temporária será exigida no primeiro acesso.</p></div>
        <div class="form-actions full"><button class="button secondary" type="button" data-action="close-create-user">Cancelar</button><button class="button" type="submit">${iconSvg("plus")} Criar usuário</button></div>
      </form>
    </section>
  </div>`;
}

function renderResearchers() {
  const accounts = state.researchers || [];
  return `<section class="panel team-panel">
    <div class="panel-heading inline-heading"><div><p class="eyebrow">${isSuperuser() ? "Controle de acesso" : "Equipe em campo"}</p><h2>${isSuperuser() ? "Usuários cadastrados" : "Pesquisadores cadastrados"}</h2><p>${accounts.length} perfil(is) no cadastro.${isSuperuser() ? " Use a busca do navegador para localizar nomes ou e-mails." : " Esta página é somente para consulta."}</p></div><div class="heading-actions">${isSuperuser() ? `<button class="button small" type="button" data-action="open-create-user">${iconSvg("plus")} Criar usuário</button>` : ""}<button class="button secondary small" type="button" data-action="refresh-researchers" ${state.researchersLoading || !navigator.onLine ? "disabled" : ""}>${iconSvg("refresh")} ${state.researchersLoading ? "Atualizando…" : "Atualizar"}</button></div></div>
    ${state.researchersError && !state.createUserModalOpen ? `<div class="callout error"><p>${escapeHtml(state.researchersError)}</p></div>` : ""}
    <div class="researcher-list">
      ${state.researchersLoading && !state.researchers ? `<div class="dashboard-loading"><span class="spinner"></span><p>Carregando equipe…</p></div>` : ""}
      ${accounts.map(renderManagedUserCard).join("") || (!state.researchersLoading ? `<div class="empty-state">Nenhum usuário cadastrado.</div>` : "")}
    </div>
    ${renderCreateUserModal()}
  </section>`;
}

function renderAdminAnalytics() {
  const stateBlock = adminDataState();
  if (stateBlock) return stateBlock;
  const report = adminReport();
  const valid = report.totals.valid || 0;
  const analytics = buildChoiceAnalytics(state.adminDocuments || [], {
    phase: state.adminPhase,
    gender: state.adminAnalyticsGender,
    treatmentGroup: state.adminAnalyticsTreatment,
    questionnaireVersion: state.adminAnalyticsVersion || state.questionnaire.questionnaire_version
  });
  return `${phaseTabs()}${centralStatus(report)}
    <div class="analytics-section-heading"><p class="eyebrow">Perfil da amostra</p><h2>Quem respondeu à pesquisa</h2></div>
    <div class="dashboard-charts analytics-grid">
      ${dashboardDistribution("Gênero", report.distributions.gender, valid)}
      ${dashboardDistribution("Faixa etária", report.distributions.age_band, valid, 6)}
      ${dashboardDistribution("Hábito de uso do metrô", report.distributions.metro_habit, valid)}
      ${dashboardDistribution("Grupo da abordagem", report.distributions.treatment_group, valid)}
      ${dashboardDistribution("Município", report.distributions.city, valid)}
      ${dashboardDistribution("Bairros com mais entrevistas", report.distributions.neighborhood, valid, 15)}
      ${dashboardDistribution("Dispositivos", report.distributions.device, valid, 12)}
    </div>
    <div class="analytics-section-heading"><p class="eyebrow">Preferência declarada</p><h2>Escolhas, escalas e atributos</h2><p>Use o filtro para comparar como as decisões mudam entre os grupos.</p></div>
    ${analyticsFilter(analytics)}
    <div class="dashboard-charts analytics-grid choice-analytics-grid">
      ${dashboardDistribution("Alternativas escolhidas", analytics.choices, analytics.decisions, 2)}
      ${choiceByGenderChart(analytics.choice_by_gender)}
      ${psychometricCard("security", analytics.psychometrics.security)}
      ${psychometricCard("comfort", analytics.psychometrics.comfort)}
      ${psychometricCard("trust", analytics.psychometrics.trust)}
      ${psychometricChoiceProfile(analytics.psychometric_by_choice)}
    </div>
    ${attributeInfluence(analytics.attributes)}`;
}

function researcherForDocument(document) {
  return (state.researchers || []).find(item => item.id === document.synced_by);
}

function renderAdminData() {
  const documents = [...(state.adminDocuments || [])].filter(document => {
    const flags = recordFlags(document);
    return state.adminRecordKind === "all"
      || (state.adminRecordKind === "test" && flags.is_test)
      || (state.adminRecordKind === "duplicate" && flags.duplicate_attempt)
      || (state.adminRecordKind === "real" && !flags.is_test && !flags.duplicate_attempt);
  }).sort((a, b) => String(b.finished_at || b.$updatedAt).localeCompare(String(a.finished_at || a.$updatedAt)));
  return `<div class="export-grid">
    <article class="export-card"><span>${iconSvg("download")}</span><div><h2>Base para análise</h2><p>CSV em formato largo, com uma linha por pesquisa, identificação do pesquisador, localização, escolhas e escalas.</p></div><button class="button" type="button" data-action="export-supabase-csv">Baixar CSV</button></article>
    <article class="export-card"><span>${iconSvg("download")}</span><div><h2>Escolhas em painel</h2><p>CSV em formato longo, com quatro linhas por respondente e o mesmo identificador nas quatro escolhas.</p></div><button class="button" type="button" data-action="export-supabase-long-csv">Baixar CSV longo</button></article>
    <article class="export-card"><span>${iconSvg("database")}</span><div><h2>Backup completo</h2><p>Cópia JSON dos documentos centrais e do histórico de solicitações de exclusão.</p></div><button class="button secondary" type="button" data-action="export-supabase-json">Baixar backup</button></article>
  </div>
  <section class="panel admin-section">
    <div class="panel-heading inline-heading"><div><p class="eyebrow">Banco</p><h2>Entrevistas recebidas</h2><p>${documents.length} registro(s). A exclusão solicitada nunca apaga o documento original.</p></div><button class="button secondary small" type="button" data-action="refresh-admin-dashboard">Atualizar</button></div>
    <div class="form-actions" aria-label="Filtrar registros">${[["all", "Todos"], ["real", "Coleta real"], ["test", "Testes"], ["duplicate", "Duplicidades"]].map(([value, label]) => `<button type="button" class="button secondary small" data-action="filter-record-kind" data-kind="${value}" aria-pressed="${state.adminRecordKind === value}">${label}</button>`).join("")}</div>
    <p class="field-help">Os CSVs preservam todos os registros para auditoria. Nas análises, filtre analysis_eligible=true e escolhas respondidas.</p>
    <div class="table-wrap"><table class="admin-table"><thead><tr><th>ID</th><th>Finalizada</th><th>Pesquisador</th><th>Fase</th><th>Bairro</th><th>Situação</th></tr></thead><tbody>
      ${documents.slice(0, 100).map(document => { const researcher = researcherForDocument(document); return `<tr><td class="mono" title="${escapeHtml(document.interview_id)}">${escapeHtml(shortId(document.interview_id))}</td><td>${escapeHtml(formatDateTime(document.finished_at))}</td><td><div class="table-person"><strong>${escapeHtml(document.researcher_name || researcher?.name || document.interviewer_code)}</strong><span>${escapeHtml(document.researcher_email || researcher?.email || "")}</span></div></td><td>${escapeHtml(document.phase)}</td><td>${escapeHtml(document.origin_neighborhood)}</td><td>${document.deletion_requested ? `<span class="status-badge deleted">Exclusão solicitada</span>` : recordFlags(document).duplicate_attempt ? `<span class="status-badge">Duplicidade</span>` : recordFlags(document).is_test ? `<span class="status-badge">Teste</span>` : `<span class="status-badge synced">Coleta real</span>`}</td></tr>`; }).join("") || `<tr><td colspan="6" class="empty-state">Nenhuma entrevista sincronizada.</td></tr>`}
    </tbody></table></div>
  </section>`;
}

async function renderAdminSettings() {
  const [summary, settings] = await Promise.all([dashboardSummary(), getSettings()]);
  return `<div class="settings-grid">
    <section class="panel"><div class="panel-heading"><p class="eyebrow">Este navegador</p><h2>Identificação local</h2><p>Use estas opções somente se este computador também for utilizado em campo.</p></div>
      ${state.formError ? `<p class="form-error">${escapeHtml(state.formError)}</p>` : ""}
      <form id="settings-form" class="form-grid"><div class="form-field"><label for="interviewer-code">Código de campo</label><input id="interviewer-code" name="interviewerCode" type="text" maxlength="50" value="${escapeHtml(settings.interviewerCode)}" required></div><div class="form-field"><label for="device-id">Identificador do dispositivo</label><input id="device-id" name="deviceId" type="text" maxlength="50" value="${escapeHtml(settings.deviceId)}" required></div><label class="radio-option full"><input type="checkbox" name="autoSync" ${settings.autoSync ? "checked" : ""}><span>Enviar automaticamente ao concluir e tentar novamente a cada 30 segundos</span></label><div class="form-field full"><button class="button" type="submit">Salvar identificação</button></div></form>
      <dl class="mini-metrics"><div><dt>Registros locais</dt><dd>${summary.total}</dd></div><div><dt>Pendentes</dt><dd>${summary.pending}</dd></div><div><dt>Enviados</dt><dd>${summary.synced}</dd></div></dl>
    </section>
    <section class="panel"><div class="panel-heading"><p class="eyebrow">Versões publicadas</p><h2>Configuração da pesquisa</h2></div><dl class="definition-list"><div><dt>Fase ativa</dt><dd>${escapeHtml(state.experiment.experiment.title)}</dd></div><div><dt>Aplicativo</dt><dd>v${escapeHtml(state.experiment.experiment.app_version)}</dd></div><div><dt>Plano experimental</dt><dd>${state.experiment.blocks.length} blocos × ${state.experiment.design.tasks_per_block} cenários</dd></div><div><dt>Questionário</dt><dd>versão ${escapeHtml(state.questionnaire.questionnaire_version)}</dd></div><div><dt>Catálogo de bairros</dt><dd>versão ${escapeHtml(state.neighborhoodCatalog.catalog_version)} · ${state.neighborhoodCatalog.municipalities.reduce((sum, municipality) => sum + municipality.neighborhoods.length, 0)} bairros</dd></div></dl></section>
    <section class="panel"><div class="panel-heading"><p class="eyebrow">Diagnóstico</p><h2>Serviço do banco</h2><p>Verifique autenticação e acesso ao banco de dados.</p></div><dl class="definition-list"><div><dt>Conta conectada</dt><dd>${escapeHtml(state.session?.email || "—")}</dd></div><div><dt>Conectividade</dt><dd>${navigator.onLine ? "Internet disponível" : "Sem conexão"}</dd></div><div><dt>Última atualização</dt><dd>${state.adminLastRefresh ? escapeHtml(formatDateTime(state.adminLastRefresh)) : "—"}</dd></div></dl><div class="form-actions"><button class="button secondary" type="button" data-action="test-connection">Testar conexão</button><button class="button secondary" type="button" data-action="export-backup">Backup local</button></div></section>
  </div>`;
}

const ADMIN_NAVIGATION = Object.freeze([
  { route: "admin-overview", label: "Visão geral", icon: "home" },
  { route: "admin-users", label: "Usuários", icon: "users" },
  { route: "admin-analytics", label: "Análises", icon: "chart" },
  { route: "admin-data", label: "Dados e backup", icon: "database" },
  { route: "admin-settings", label: "Configurações", icon: "settings", superuserOnly: true },
  { route: "researcher-home", label: "Coleta no dispositivo", icon: "train", superuserOnly: true }
]);

const ADMIN_TITLES = Object.freeze({
  "admin-overview": ["Visão geral", "Acompanhe o andamento da coleta em todos os dispositivos."],
  "admin-users": ["Usuários", "Consulte a equipe e, como superusuário, gerencie perfis de acesso."],
  "admin-analytics": ["Análises da amostra", "Explore o perfil das entrevistas válidas recebidas até agora."],
  "admin-data": ["Dados e backup", "Exporte uma base pronta para análise ou preserve uma cópia integral."],
  "admin-settings": ["Configurações da pesquisa", "Área exclusiva do superusuário para configurações e diagnóstico."]
});

async function renderAdmin() {
  const route = ADMIN_TITLES[state.route] && (state.route !== "admin-settings" || isSuperuser()) ? state.route : "admin-overview";
  const [title, subtitle] = ADMIN_TITLES[route];
  const navigation = ADMIN_NAVIGATION.filter(item => !item.superuserOnly || isSuperuser());
  let content;
  if (route === "admin-users") content = renderResearchers();
  else if (route === "admin-analytics") content = renderAdminAnalytics();
  else if (route === "admin-data") content = renderAdminData();
  else if (route === "admin-settings") content = await renderAdminSettings();
  else content = renderAdminOverview();
  return `<div class="admin-shell">
    <aside class="admin-sidebar"><div class="admin-brand"><div class="brand-mark">PD</div><div><strong>PDP</strong><span>${isSuperuser() ? "Superusuário" : "Administração"}</span></div></div><nav class="admin-nav" aria-label="Painel administrativo">${navigation.map(item => `<button class="admin-nav-item ${route === item.route ? "active" : ""}" type="button" data-action="admin-navigate" data-route="${item.route}">${iconSvg(item.icon)}<span>${item.label}</span></button>`).join("")}</nav><div class="admin-account"><span>${escapeHtml(state.session?.username || "Administrador")}</span><small>${escapeHtml(accountRoleLabel(state.session?.role))} · ${escapeHtml(state.session?.email || "")}</small><button type="button" data-action="logout">Sair da conta</button></div></aside>
    <main class="admin-main"><header class="admin-toolbar"><div><p class="eyebrow">${isSuperuser() ? "Portal do superusuário" : "Painel administrativo"}</p><h1>${escapeHtml(title)}</h1><p>${escapeHtml(subtitle)}</p></div><div class="admin-toolbar-actions">${connectionBadge()}${themeButton("button secondary small")}<button class="button secondary small" type="button" data-action="refresh-admin-dashboard" ${state.adminDashboardLoading || !navigator.onLine ? "disabled" : ""}>${iconSvg("refresh")} ${state.adminDashboardLoading ? "Atualizando…" : "Atualizar agora"}</button></div></header><div class="admin-content">${state.adminDashboardError && state.adminDashboard ? `<div class="callout error"><p>Não foi possível atualizar agora: ${escapeHtml(state.adminDashboardError)}</p></div>` : ""}${content}</div></main>
  </div>`;
}

function renderBusy(message) {
  return appFrame(`
    <main class="page">
      <section class="panel sync-panel">
        <div class="brand-mark" style="margin-inline:auto" aria-hidden="true">…</div>
        <h2>${escapeHtml(message)}</h2>
        <p>Não feche o aplicativo.</p>
      </section>
    </main>
  `, { title: "Experimento PD", subtitle: "Processando" });
}

async function render(options = {}) {
  const previousScroll = globalThis.scrollY;
  try {
    let html;
    switch (state.route) {
      case "login": html = renderLogin(); break;
      case "change-password": html = renderRequiredPasswordChange(); break;
      case "researcher-home": html = await renderResearcherHome(); break;
      case "survey-context": html = renderFieldContext(); break;
      case "survey-profile": html = renderProfile(); break;
      case "survey-intro": html = renderScenarioIntro(); break;
      case "survey-choice": html = renderScenario(); break;
      case "survey-mobility": html = renderMobility(); break;
      case "survey-security": html = renderPsychometrics("security"); break;
      case "survey-comfort": html = renderPsychometrics("comfort"); break;
      case "survey-trust": html = renderPsychometrics("trust"); break;
      case "survey-review": html = renderReview(); break;
      case "survey-complete": html = renderComplete(); break;
      case "survey-duplicate": html = appFrame(`<main class="page"><section class="panel"><h1>Obrigado pela participação!</h1><p>Como você já respondeu recentemente a esta pesquisa, não precisamos repetir as perguntas.</p><p>A ocorrência foi salva neste dispositivo para auditoria e será enviada quando houver conexão.</p><button class="button" data-action="go-home">Voltar ao início</button></section></main>`); break;
      case "delete-last": html = await renderDeletion(); break;
      case "sync": html = await renderSync(); break;
      case "admin-overview":
      case "admin-users":
      case "admin-analytics":
      case "admin-data":
      case "admin-settings": html = await renderAdmin(); break;
      case "busy": html = renderBusy("Preparando a entrevista…"); break;
      default: html = state.session
        ? (state.session.mustChangePassword ? renderRequiredPasswordChange() : (isAdminPortalRole() ? await renderAdmin() : await renderResearcherHome()))
        : renderLogin();
    }
    app.innerHTML = html;
    document.body.classList.toggle("modal-open", state.createUserModalOpen && state.route === "admin-users");
    document.title = state.route.startsWith("admin-") ? `${ADMIN_TITLES[state.route]?.[0] || "Administração"} · PDP` : "PDP";
    window.scrollTo({ top: options.preserveScroll ? previousScroll : 0, behavior: "instant" });
  } catch (error) {
    console.error(error);
    app.innerHTML = `<main class="startup-screen"><div class="brand-mark">!</div><h1>Não foi possível abrir a tela</h1><p>${escapeHtml(error.message)}</p><button class="button" type="button" data-action="reload">Tentar novamente</button></main>`;
  }
}

async function createInterview() {
  if (state.busy) return;
  state.busy = true;
  try {
    state.route = "busy";
    await render();

    const settings = await getSettings();
    const id = createUuid();
    const startedAt = nowIso();
    const blockIndex = await claimNextBlock(state.experiment.blocks.length);
    const block = deepClone(state.experiment.blocks[blockIndex]);
    assignAlternativeOrder(block.tasks);
    const location = await getCurrentLocation({ timeout: 8000 });

    const interview = {
      id,
      respondentId: id,
      collectionVersion: COLLECTION_VERSION,
      is_test: false,
      duplicate_attempt: false,
      revision: 0,
      status: "draft",
      syncStatus: "draft",
      experimentId: state.experiment.experiment.id,
      experimentVersion: state.experiment.experiment.version,
      questionnaireVersion: state.questionnaire.questionnaire_version,
      appVersion: state.experiment.experiment.app_version,
      phase: state.experiment.experiment.phase,
      interviewerCode: state.session?.email || settings.interviewerCode,
      deviceId: settings.deviceId,
      startedAt,
      finishedAt: null,
      durationSeconds: null,
      timezoneOffset: localTimezoneOffset(),
      location,
      designSnapshot: block,
      experimentQuestionnaireSnapshot: deepClone(state.experiment.questionnaire),
      experimentConfigGeneratedAt: state.experiment.generated_at,
      questionnaireSnapshot: deepClone(state.questionnaire),
      responses: {
        field_context: {},
        profile: {},
        choices: {},
        mobility: {},
        psychometrics: { security: {}, comfort: {}, trust: {} }
      },
      audit: [{ type: "created", at: startedAt }],
      deletion: { requested: false }
    };

    state.activeInterview = await saveInterview(interview);
    state.choiceIndex = 0;
    state.draftSaveState = "saved";
    state.busy = false;
    await navigate("survey-context");
  } catch (error) {
    state.route = "researcher-home";
    showToast(`Não foi possível iniciar a pesquisa: ${error.message}`, "error");
  } finally {
    state.busy = false;
    await render();
  }
}

async function beginEditingLatest() {
  const interview = await getLatestCompletedInterview();
  if (!interview) throw new Error("Nenhuma entrevista concluída foi encontrada.");
  interview.editBaseRevision = Number(interview.revision || 0);
  interview.status = "draft";
  interview.audit = [...(interview.audit || []), { type: "edit_started", at: nowIso(), base_revision: interview.editBaseRevision }];
  state.activeInterview = await saveInterview(interview);
  state.choiceIndex = 0;
  state.draftSaveState = "saved";
  navigate("survey-context");
}

async function resumeDraft(id) {
  const summary = await dashboardSummary();
  const interview = summary.interviews.find(item => item.id === id && item.status === "draft");
  if (!interview) throw new Error("O rascunho não foi encontrado.");
  state.activeInterview = interview;
  state.choiceIndex = 0;
  state.draftSaveState = "saved";
  await navigate("survey-context");
}

async function abandonInterview() {
  if (state.busy || !state.activeInterview || state.activeInterview.editBaseRevision != null) return;
  state.busy = true;
  try {
    await flushDraftAutosave();
    const abandonedAt = nowIso();
    const partial = deepClone(state.activeInterview);
    partial.status = "abandoned";
    partial.syncStatus = "local_partial";
    partial.abandonedAt = abandonedAt;
    partial.abandonedRoute = state.route;
    partial.durationSeconds = durationSeconds(partial.startedAt, abandonedAt);
    partial.audit = [...(partial.audit || []), { type: "abandoned", at: abandonedAt, route: state.route }];
    await saveInterview(partial);
    state.activeInterview = null;
    showToast("Parcial salvo neste aparelho. Você já pode iniciar outra pesquisa.", "success");
    await navigate("researcher-home");
  } finally {
    state.busy = false;
    await render({ preserveScroll: true });
  }
}

async function resumePartial(id) {
  const summary = await dashboardSummary();
  if (summary.interviews.some(item => item.status === "draft")) throw new Error("Salve ou encerre a entrevista em andamento antes de retomar outra.");
  const partial = summary.interviews.find(item => item.id === id && item.status === "abandoned");
  if (!partial) throw new Error("A entrevista parcial não foi encontrada.");
  partial.status = "draft";
  partial.syncStatus = "draft";
  partial.audit = [...(partial.audit || []), { type: "partial_resumed", at: nowIso() }];
  state.activeInterview = await saveInterview(partial);
  state.choiceIndex = 0;
  await navigate("survey-context");
}

async function persistActive() {
  if (!state.activeInterview) return;
  state.draftSaveState = "saving";
  updateDraftSaveIndicator();
  try {
    state.activeInterview = await saveInterview(state.activeInterview);
    state.lastSavedAt = nowIso();
    state.draftSaveState = "saved";
  } catch (error) {
    state.draftSaveState = "error";
    throw error;
  } finally {
    updateDraftSaveIndicator();
  }
}

async function savePartialCurrentScreen() {
  const interview = state.activeInterview;
  if (!interview) return;
  const form = app.querySelector("form");
  if (!form) return persistActive();
  const data = new FormData(form);

  if (state.route === "survey-context") {
    const context = interview.questionnaireSnapshot.field_context;
    if (data.get("recent_participation")) interview.responses.recent_participation = data.get("recent_participation");
    const approachType = data.get(context.approach_type.id);
    if (approachType) interview.responses.field_context.approach_type = approachType;
    if (approachType === "embarcado") {
      const lineCode = String(data.get(context.bus_line.id) || "");
      const line = BUS_LINES.find(option => option.value === lineCode);
      if (line) {
        const treatmentGroup = line.treatment_group === "pitaguari" ? "pitaguari" : "controle";
        Object.assign(interview.responses.field_context, {
          treatment_group: treatmentGroup,
          TRAT_i: treatmentGroup === "pitaguari" ? 1 : 0,
          line_or_location: line.value,
          bus_line_label: line.label,
          pitaguari_serves_stop: null,
          reference_station: null
        });
      }
    } else if (approachType === "ponto") {
      const stopAnswer = data.get(context.pitaguari_serves_stop.id);
      if (stopAnswer) {
        const pitaguariServesStop = stopAnswer === "sim";
        Object.assign(interview.responses.field_context, {
          treatment_group: pitaguariServesStop ? "pitaguari" : "controle",
          TRAT_i: pitaguariServesStop ? 1 : 0,
          line_or_location: "Parada de ônibus",
          bus_line_label: null,
          pitaguari_serves_stop: pitaguariServesStop,
          reference_station: null
        });
      }
    }
  }

  if (["survey-profile", "survey-mobility"].includes(state.route)) {
    const gender = data.get(interview.questionnaireSnapshot.profile.gender.id);
    const rawAge = data.get(interview.questionnaireSnapshot.profile.age.id);
    const age = Number(rawAge);
    if (gender) interview.responses.profile.gender_identity = gender;
    if (rawAge !== "" && Number.isInteger(age) && age >= 0) interview.responses.profile.age_years = age;
  }
  if (["survey-profile", "survey-mobility"].includes(state.route)) {
    const habit = data.get(interview.questionnaireSnapshot.mobility.metro_habit.id);
    if (habit) interview.responses.mobility.metro_habit = habit;
    if (data.get(interview.questionnaireSnapshot.mobility.neighborhood.id) === "outro") {
      Object.assign(interview.responses.mobility, {
        origin_neighborhood_category: "outro",
        origin_neighborhood_other: String(data.get("origin_neighborhood_other") || ""),
        origin_neighborhood: String(data.get("origin_neighborhood_other") || ""),
        origin_city: String(data.get("origin_city_other") || "")
      });
    }
    try {
      Object.assign(
        interview.responses.mobility,
        resolveNeighborhoodSelection(
          data.get(interview.questionnaireSnapshot.mobility.neighborhood.id),
          state.neighborhoodCatalog,
          { ...interview.responses.mobility, origin_neighborhood_other: data.get("origin_neighborhood_other"), origin_city_other: data.get("origin_city_other") }
        )
      );
    } catch {
      // Mantém o rascunho anterior quando nenhuma seleção válida foi feita.
    }
  }
  if (state.route === "survey-review") interview.is_test = data.has("is_test");
  if (PSYCHOMETRIC_KINDS.some(kind => state.route === `survey-${kind}`)) {
    const kind = state.route.replace("survey-", "");
    const section = interview.questionnaireSnapshot.psychometrics[kind];
    for (const item of section.items) {
      const value = data.get(item.id);
      if (value) interview.responses.psychometrics[kind][item.id] = Number(value);
    }
  }
  await persistActive();
}

function updateDraftSaveIndicator() {
  const indicator = document.querySelector("#draft-save-indicator");
  if (indicator) indicator.outerHTML = draftSaveIndicator();
}

function scheduleDraftAutosave(delay = 350) {
  if (!state.activeInterview || !state.route.startsWith("survey-")) return;
  if (draftSaveTimer) clearTimeout(draftSaveTimer);
  state.draftSaveState = "saving";
  updateDraftSaveIndicator();
  draftSaveTimer = setTimeout(() => {
    draftSaveTimer = null;
    draftSaveChain = draftSaveChain
      .catch(() => {})
      .then(() => savePartialCurrentScreen())
      .catch(error => {
        console.error("Salvamento automático:", error);
        state.draftSaveState = "error";
        updateDraftSaveIndicator();
      });
  }, delay);
}

async function flushDraftAutosave() {
  if (draftSaveTimer) {
    clearTimeout(draftSaveTimer);
    draftSaveTimer = null;
  }
  draftSaveChain = draftSaveChain
    .catch(() => {})
    .then(() => savePartialCurrentScreen());
  return draftSaveChain;
}

async function guideToFirstIncomplete() {
  await flushDraftAutosave();
  const missing = findFirstIncomplete(state.activeInterview, state.activeInterview.questionnaireSnapshot);
  if (!missing) return false;
  if (Number.isInteger(missing.choiceIndex)) state.choiceIndex = missing.choiceIndex;
  state.route = missing.route;
  state.formError = missing.message;
  await render();
  showToast(missing.message, "error");
  if (missing.focusName) {
    requestAnimationFrame(() => {
      const field = [...app.querySelectorAll("[name]")].find(item => item.name === missing.focusName);
      if (field) showFieldAttention(field, missing.message);
    });
  }
  return true;
}

async function closeDuplicateAttempt() {
  await flushDraftAutosave();
  const interview = state.activeInterview;
  const at = nowIso();
  interview.duplicate_attempt = true;
  interview.is_test = false;
  interview.revision = Number(interview.revision || 0) + 1;
  interview.status = "screened_out";
  interview.syncStatus = "pending";
  interview.finishedAt = at;
  interview.durationSeconds = durationSeconds(interview.startedAt, at);
  interview.audit = [...(interview.audit || []), { type: "duplicate_screened_out", at }];
  state.activeInterview = await saveInterview(interview);
  await enqueueInterview(state.activeInterview);
  state.activeInterview = null;
  await navigate("survey-duplicate");
}

async function finishInterview() {
  const interview = state.activeInterview;
  validateCompleteInterview(interview, interview.questionnaireSnapshot);
  const finishedAt = nowIso();
  interview.revision = Number(interview.editBaseRevision ?? interview.revision ?? 0) + 1;
  delete interview.editBaseRevision;
  interview.status = "pending";
  interview.syncStatus = "pending";
  interview.finishedAt = finishedAt;
  interview.durationSeconds = durationSeconds(interview.startedAt, finishedAt);
  interview.audit = [...(interview.audit || []), { type: "finished", at: finishedAt, revision: interview.revision }];
  state.activeInterview = await saveInterview(interview);
  await enqueueInterview(state.activeInterview);
  state.activeInterview = null;
  state.syncResult = null;
  if (navigator.onLine && !state.session?.offline) {
    try {
      const settings = await getSettings();
      if (settings.autoSync) state.syncResult = await synchronizePending(settings, state.experiment);
    } catch (error) {
      console.warn("Envio automático adiado:", error);
    }
  }
  state.busy = false;
  navigate("survey-complete");
}

async function requestDeletion(id, reason) {
  const latest = await getLatestCompletedInterview();
  if (!latest || latest.id !== id) throw new Error("A última entrevista mudou; atualize a tela e tente novamente.");
  latest.deletion = {
    requested: true,
    reason,
    requestedAt: nowIso(),
    requestedBy: state.session.username,
    version: Number(latest.deletion?.version || 0) + 1,
    syncStatus: "pending"
  };
  latest.audit = [...(latest.audit || []), { type: "deletion_requested", at: latest.deletion.requestedAt, reason }];
  const saved = await saveInterview(latest);
  await enqueueDeletion(saved);
  showToast("Solicitação de exclusão registrada neste dispositivo.", "success");
  await navigate("researcher-home");
  attemptAutomaticSync();
}

async function exportBackup() {
  const backup = await buildLocalBackup(state.experiment);
  const settings = await getSettings();
  const filename = `pdapp-backup-${settings.deviceId}-${new Date().toISOString().slice(0, 10)}.json`;
  downloadJson(filename, backup);
  showToast("Backup exportado.", "success");
}

async function refreshResearchers() {
  state.researchersLoading = true;
  state.researchersError = "";
  await render();
  try {
    state.researchers = await listResearchers();
    if (state.adminDocuments) state.adminDashboard = buildAdminDashboard(state.adminDocuments, state.researchers);
  } catch (error) {
    state.researchersError = error.message;
  } finally {
    state.researchersLoading = false;
    await render();
  }
}

async function exportSupabaseData(format) {
  const [documents, deletions] = await Promise.all([
    fetchAllInterviewDocuments(),
    format === "json" ? fetchAllDeletionRequests() : Promise.resolve([])
  ]);
  const date = new Date().toISOString().slice(0, 10);
  state.adminDocuments = documents;
  if (format === "json") {
    downloadJson(`pdp-backup-completo-${date}.json`, {
      backup_schema_version: "2.0.0",
      exported_at: nowIso(),
      project_ref: supabaseConfig.projectRef,
      database: "postgres",
      total: documents.length,
      interviews: documents,
      deletion_requests: deletions
    });
  } else if (format === "long") {
    downloadText(`pdp-escolhas-painel-${date}.csv`, buildChoiceLongCsv(documents, state.researchers || []), "text/csv;charset=utf-8");
  } else {
    downloadText(`pdp-base-larga-${date}.csv`, buildWideCsv(documents, state.researchers || []), "text/csv;charset=utf-8");
  }
  showToast(`${documents.length} entrevista(s) exportada(s).`, "success");
}

async function runSynchronization() {
  state.busy = true;
  state.formError = "";
  state.syncResult = null;
  if (canRunSurveys()) state.route = "sync";
  await render();

  try {
    const settings = await getSettings();
    const result = await synchronizePending(settings, state.experiment, progress => {
      state.syncProgress = progress;
      render();
    });
    state.syncResult = result;
    if (result.errors) showToast(`${result.errors} item(ns) não foram enviados.`, "error");
    else showToast(`${result.success} item(ns) sincronizado(s).`, "success");
  } catch (error) {
    state.formError = error.message;
    showToast(error.message, "error");
  } finally {
    state.busy = false;
    state.syncProgress = null;
    await render();
  }
}

async function refreshAdminDashboard(options = {}) {
  state.adminDashboardLoading = true;
  state.adminDashboardError = "";
  if (!options.background) await render();
  try {
    const researchersRequest = options.background && state.researchers
      ? Promise.resolve(state.researchers)
      : listResearchers();
    const [response, researchers] = await Promise.all([fetchAdminDashboard(), researchersRequest]);
    state.researchers = researchers;
    state.adminDocuments = response.documents;
    state.adminDashboard = buildAdminDashboard(response.documents, researchers);
    state.adminLastRefresh = nowIso();
    if (!options.background) showToast("Dados da coleta atualizados.", "success");
  } catch (error) {
    state.adminDashboardError = error.message;
    if (!options.background) showToast(error.message, "error");
  } finally {
    state.adminDashboardLoading = false;
    await render({ preserveScroll: Boolean(options.background) });
  }
}

async function handleAction(action, element) {
  switch (action) {
    case "reload": globalThis.location.reload(); break;
    case "toggle-theme":
      toggleTheme();
      await render({ preserveScroll: true });
      break;
    case "logout":
      await signOut();
      stopBackgroundJobs();
      state.session = null;
      state.activeInterview = null;
      navigate("login");
      break;
    case "new-interview": await createInterview(); break;
    case "edit-latest": await beginEditingLatest(); break;
    case "resume-draft": await resumeDraft(element.dataset.id); break;
    case "delete-latest": navigate("delete-last"); break;
    case "open-sync": navigate("sync"); break;
    case "go-home": navigate(isAdminPortalRole() ? "admin-overview" : "researcher-home"); break;
    case "open-admin-portal": navigate("admin-overview"); break;
    case "admin-navigate":
      if (element.dataset.route === "admin-settings" && !isSuperuser()) throw new Error("Esta área é exclusiva do superusuário.");
      navigate(element.dataset.route);
      break;
    case "toggle-password": {
      const input = document.getElementById(element.dataset.target);
      if (!input) break;
      const visible = input.type === "password";
      input.type = visible ? "text" : "password";
      element.setAttribute("aria-pressed", String(visible));
      element.setAttribute("aria-label", visible ? "Ocultar senha" : "Mostrar senha");
      element.innerHTML = `${iconSvg(visible ? "eyeOff" : "eye")}<span${element.closest(".compact") ? ' class="sr-only"' : ""}>${visible ? "Ocultar" : "Mostrar"}</span>`;
      input.focus({ preventScroll: true });
      break;
    }
    case "install-app": {
      if (state.installPrompt) {
        state.installPrompt.prompt();
        const choice = await state.installPrompt.userChoice;
        state.installPrompt = null;
        state.installHelpVisible = choice.outcome !== "accepted";
      } else {
        state.installHelpVisible = !state.installHelpVisible;
      }
      await render({ preserveScroll: true });
      break;
    }
    case "export-backup": await exportBackup(); break;
    case "export-supabase-json": await exportSupabaseData("json"); break;
    case "export-supabase-csv": await exportSupabaseData("csv"); break;
    case "export-supabase-long-csv": await exportSupabaseData("long"); break;
    case "refresh-researchers": await refreshResearchers(); break;
    case "open-create-user":
      if (!isSuperuser()) throw new Error("Somente o superusuário pode criar contas.");
      state.researchersError = "";
      state.createUserModalOpen = true;
      await render({ preserveScroll: true });
      requestAnimationFrame(() => document.querySelector("#managed-user-role")?.focus());
      break;
    case "close-create-user":
      state.createUserModalOpen = false;
      state.researchersError = "";
      await render({ preserveScroll: true });
      requestAnimationFrame(() => document.querySelector('[data-action="open-create-user"]')?.focus());
      break;
    case "edit-managed-user":
      if (!isSuperuser()) throw new Error("Somente o superusuário pode editar contas.");
      state.editingUserId = element.dataset.userId;
      await render({ preserveScroll: true });
      break;
    case "cancel-edit-user":
      state.editingUserId = null;
      await render({ preserveScroll: true });
      break;
    case "toggle-researcher": {
      if (!isSuperuser()) throw new Error("Somente o superusuário pode alterar contas.");
      const response = await setManagedUserStatus(element.dataset.userId, element.dataset.status === "true");
      showToast(response.message, "success");
      await refreshResearchers();
      break;
    }
    case "abandon-interview": await abandonInterview(); break;
    case "resume-partial": await resumePartial(element.dataset.id); break;
    case "intro-back": await navigate("survey-profile"); break;
    case "start-scenarios":
      state.activeInterview.scenarioBriefingReadAt = nowIso();
      await persistActive();
      await navigate("survey-choice");
      break;
    case "save-exit":
      await flushDraftAutosave();
      state.activeInterview = null;
      showToast("Rascunho salvo neste dispositivo.", "success");
      navigate("researcher-home");
      break;
    case "choose-alternative": {
      const task = state.activeInterview.designSnapshot.tasks[state.choiceIndex];
      const alternative = task.alternatives.find(item => item.id === element.dataset.alternative);
      state.activeInterview.responses.choices[task.task_id] = {
        task_id: task.task_id,
        scenario_id: task.scenario_id,
        displayed_alternatives: orderedAlternatives(task).map(item => item.id),
        presentation_version: 2,
        selected_alternative: alternative.id,
        selected_label: alternative.label,
        answered_at: nowIso()
      };
      await persistActive();
      await render();
      break;
    }
    case "previous-scenario":
      if (state.choiceIndex > 0) {
        state.choiceIndex -= 1;
        await render();
      } else navigate("survey-intro");
      break;
    case "next-scenario": {
      const tasks = state.activeInterview.designSnapshot.tasks;
      const task = tasks[state.choiceIndex];
      if (!state.activeInterview.responses.choices[task.task_id]) {
        state.formError = "Escolha uma alternativa antes de continuar.";
        await render();
      } else if (state.choiceIndex < tasks.length - 1) {
        state.choiceIndex += 1;
        state.formError = "";
        await render();
      } else navigate("survey-security");
      break;
    }
    case "back-to-choices":
      state.choiceIndex = state.activeInterview.designSnapshot.tasks.length - 1;
      navigate("survey-choice");
      break;
    case "back-to-context": navigate("survey-context"); break;
    case "psychometric-back": {
      const kindIndex = PSYCHOMETRIC_KINDS.indexOf(element.dataset.kind);
      state.choiceIndex = state.activeInterview.designSnapshot.tasks.length - 1;
      navigate(kindIndex <= 0 ? "survey-choice" : `survey-${PSYCHOMETRIC_KINDS[kindIndex - 1]}`);
      break;
    }
    case "back-to-trust": navigate("survey-trust"); break;
    case "review-edit-context": navigate("survey-context"); break;
    case "review-edit-profile": navigate("survey-profile"); break;
    case "review-edit-mobility": navigate("survey-mobility"); break;
    case "review-edit-choices": state.choiceIndex = 0; navigate("survey-choice"); break;
    case "review-edit-security": navigate("survey-security"); break;
    case "review-edit-comfort": navigate("survey-comfort"); break;
    case "review-edit-trust": navigate("survey-trust"); break;
    case "finish-interview":
      if (await guideToFirstIncomplete()) break;
      state.busy = true;
      try { await finishInterview(); }
      catch (error) { state.busy = false; state.formError = error.message; await render(); }
      break;
    case "run-sync": await runSynchronization(); break;
    case "refresh-admin-dashboard": await refreshAdminDashboard(); break;
    case "filter-record-kind":
      state.adminRecordKind = element.dataset.kind;
      await render({ preserveScroll: true });
      break;
    case "select-admin-phase":
      state.adminPhase = element.dataset.phase;
      await render({ preserveScroll: true });
      break;
    case "select-analytics-version":
      state.adminAnalyticsVersion = Number(element.dataset.version);
      await render({ preserveScroll: true });
      break;
    case "select-analytics-gender":
      state.adminAnalyticsGender = element.dataset.gender || "all";
      await render({ preserveScroll: true });
      break;
    case "select-analytics-treatment":
      state.adminAnalyticsTreatment = element.dataset.treatment || "all";
      await render({ preserveScroll: true });
      break;
    case "test-connection": {
      state.busy = true;
      try {
        await testConnection();
        showToast("Conexão com o banco confirmada.", "success");
      } catch (error) {
        showToast(error.message, "error");
      } finally {
        state.busy = false;
      }
      break;
    }
    default: break;
  }
}

app.addEventListener("click", event => {
  if (event.target.matches("[data-modal-backdrop]")) {
    handleAction("close-create-user", event.target).catch(console.error);
    return;
  }
  const element = event.target.closest("[data-action]");
  if (!element || element.disabled) return;
  handleAction(element.dataset.action, element).catch(error => {
    console.error(error);
    state.formError = error.message;
    showToast(error.message, "error");
    render();
  });
});

app.addEventListener("keydown", event => {
  const modal = event.target.closest('[role="dialog"][aria-modal="true"]');
  if (!modal) return;
  if (event.key === "Escape") {
    event.preventDefault();
    handleAction("close-create-user", modal).catch(console.error);
    return;
  }
  if (event.key !== "Tab") return;
  const focusable = [...modal.querySelectorAll("button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])")]
    .filter(element => element.getClientRects().length);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

app.addEventListener("invalid", event => {
  const field = event.target;
  const form = field.form;
  if (!form) return;
  event.preventDefault();
  if (form.dataset.invalidHandled === "true") return;
  form.dataset.invalidHandled = "true";
  showFieldAttention(field);
  showToast("Há uma resposta pendente. O item foi destacado.", "error");
  setTimeout(() => { delete form.dataset.invalidHandled; }, 0);
}, true);

app.addEventListener("submit", event => {
  event.preventDefault();
  const form = event.target;
  const data = new FormData(form);

  (async () => {
    state.formError = "";
    if (form.id === "login-form") {
      const session = await authenticate(data.get("email"), data.get("password"));
      state.session = session;
      startBackgroundJobs();
      if (session.mustChangePassword) await navigate("change-password");
      else if (isAdminPortalRole(session.role)) {
        state.route = "admin-overview";
        await refreshAdminDashboard();
      } else {
        await navigate("researcher-home");
        attemptAutomaticSync();
      }
      return;
    }

    if (form.id === "forced-password-form") {
      const newPassword = String(data.get("newPassword") || "");
      if (newPassword !== String(data.get("confirmPassword") || "")) {
        throw new PasswordValidationError("A confirmação da nova senha não confere.");
      }
      validateNewPassword(newPassword, "A nova senha");
      state.session = await changeRequiredPassword(newPassword);
      showToast("Senha alterada com sucesso.", "success");
      startBackgroundJobs();
      if (isAdminPortalRole(state.session.role)) {
        state.route = "admin-overview";
        await refreshAdminDashboard();
      } else await navigate("researcher-home");
      return;
    }

    if (form.id === "managed-user-create-form") {
      if (!isSuperuser()) throw new Error("Somente o superusuário pode criar contas.");
      const password = validateNewPassword(data.get("password"), "A senha inicial");
      const role = String(data.get("role") || "");
      await createManagedUser({
        name: normalizeText(data.get("name")),
        email: String(data.get("email") || "").trim().toLowerCase(),
        password,
        role
      });
      form.reset();
      state.createUserModalOpen = false;
      showToast(`${role === "admin" ? "Administrador" : "Pesquisador"} criado. A troca de senha será exigida no primeiro acesso.`, "success");
      await refreshResearchers();
      return;
    }

    if (form.classList.contains("managed-user-edit-form")) {
      if (!isSuperuser()) throw new Error("Somente o superusuário pode editar contas.");
      const response = await updateManagedUser(form.dataset.userId, {
        name: normalizeText(data.get("name")),
        email: String(data.get("email") || "").trim().toLowerCase(),
        role: String(data.get("role") || "")
      });
      state.editingUserId = null;
      showToast(response.message, "success");
      await refreshResearchers();
      return;
    }

    if (form.classList.contains("researcher-reset-form")) {
      if (!isSuperuser()) throw new Error("Somente o superusuário pode redefinir senhas.");
      const password = validateNewPassword(data.get("password"), "A senha temporária");
      await resetManagedUserPassword(form.dataset.userId, password);
      form.reset();
      showToast("Senha temporária redefinida.", "success");
      await refreshResearchers();
      return;
    }

    if (form.id === "field-context-form") {
      state.activeInterview.responses.field_context = validateFieldContext(
        data,
        state.activeInterview.questionnaireSnapshot,
        state.activeInterview.responses.field_context
      );
      if (state.activeInterview.collectionVersion >= 2) {
        state.activeInterview.responses.recent_participation = readRequiredRadio(data, "recent_participation", duplicateQuestion().label);
        if (state.activeInterview.responses.recent_participation === "sim") {
          await closeDuplicateAttempt();
          return;
        }
      }
      await persistActive();
      navigate("survey-profile");
      return;
    }

    if (form.id === "profile-form") {
      const profile = validateProfile(data, state.activeInterview.questionnaireSnapshot);
      const mobility = validateMobility(data, state.activeInterview.questionnaireSnapshot, state.neighborhoodCatalog, state.activeInterview.responses.mobility);
      state.activeInterview.responses.profile = profile;
      state.activeInterview.responses.mobility = mobility;
      await persistActive();
      state.choiceIndex = 0;
      navigate("survey-intro");
      return;
    }

    if (form.id === "mobility-form") {
      state.activeInterview.responses.mobility = validateMobility(
        data,
        state.activeInterview.questionnaireSnapshot,
        state.neighborhoodCatalog,
        state.activeInterview.responses.mobility
      );
      await persistActive();
      navigate("survey-security");
      return;
    }

    if (form.id === "psychometric-form") {
      const kind = form.dataset.kind;
      const section = state.activeInterview.questionnaireSnapshot.psychometrics[kind];
      state.activeInterview.responses.psychometrics[kind] = validatePsychometrics(data, section);
      await persistActive();
      const kindIndex = PSYCHOMETRIC_KINDS.indexOf(kind);
      navigate(kindIndex < PSYCHOMETRIC_KINDS.length - 1
        ? `survey-${PSYCHOMETRIC_KINDS[kindIndex + 1]}`
        : "survey-review");
      return;
    }

    if (form.id === "delete-form") {
      const reason = normalizeText(data.get("reason"));
      if (reason.length < 5) throw new Error("Descreva o motivo da exclusão com pelo menos 5 caracteres.");
      await requestDeletion(form.dataset.id, reason);
      return;
    }

    if (form.id === "settings-form") {
      const interviewerCode = normalizeText(data.get("interviewerCode"));
      const deviceId = normalizeText(data.get("deviceId"));
      if (!interviewerCode || !deviceId) throw new Error("Informe o pesquisador e o identificador do dispositivo.");
      await saveSettings({
        interviewerCode,
        deviceId,
        autoSync: data.get("autoSync") === "on"
      });
      state.adminDashboard = null;
      state.adminDashboardError = "";
      showToast("Configuração salva neste aparelho.", "success");
      await render();
      return;
    }

  })().catch(error => {
    console.error(error);
    const preservesValues = (
      form.id === "forced-password-form"
      || form.id === "managed-user-create-form"
      || form.classList.contains("researcher-reset-form")
      || form.classList.contains("managed-user-edit-form")
    );
    if (preservesValues) {
      let invalidInput = null;
      if (error instanceof PasswordValidationError) {
        invalidInput = error.message.includes("confirmação")
          ? form.querySelector('[name="confirmPassword"]')
          : form.querySelector('[data-password-policy="true"]');
      } else if (form.classList.contains("managed-user-edit-form")) {
        if (/e-mail/i.test(error.message)) invalidInput = form.querySelector('[name="email"]');
        else if (/nome/i.test(error.message)) invalidInput = form.querySelector('[name="name"]');
        else if (/perfil/i.test(error.message)) invalidInput = form.querySelector('[name="role"]');
      }
      showInlineFormError(form, error.message, invalidInput);
      return;
    }
    if (form.id === "managed-user-create-form" || form.classList.contains("researcher-reset-form")) {
      state.researchersError = error.message;
    } else {
      state.formError = error.message;
    }
    render();
  });
});

app.addEventListener("input", event => {
  const field = event.target.closest("input, select, textarea");
  const form = field?.form;
  if (!field || !form) return;
  clearFieldAttention(field);
  const alert = form.querySelector(".form-inline-error");
  if (alert) {
    alert.hidden = true;
    alert.textContent = "";
  }

  if (field.id === "neighborhood-search") {
    const select = form.querySelector(`select[name="${state.activeInterview.questionnaireSnapshot.mobility.neighborhood.id}"]`);
    if (!select) return;
    const selectedValue = select.value;
    const matches = filterNeighborhoodEntries(state.neighborhoodCatalog, field.value);
    select.innerHTML = renderNeighborhoodOptions(
      state.neighborhoodCatalog,
      state.activeInterview.responses.mobility,
      field.value,
      selectedValue
    );
    const status = form.querySelector("#neighborhood-results-status");
    if (status) {
      status.textContent = matches.length
        ? `${matches.length} bairro(s) encontrado(s).`
        : "Nenhum bairro encontrado. Tente outro nome.";
    }
    return;
  }

  if (
    state.route.startsWith("survey-")
    && !["radio", "checkbox"].includes(field.type)
    && field.tagName !== "SELECT"
  ) {
    scheduleDraftAutosave();
  }
});

app.addEventListener("change", event => {
  const field = event.target.closest("input, select, textarea");
  if (!field?.form || !state.route.startsWith("survey-")) return;
  clearFieldAttention(field);
  if (field.name === "origin_neighborhood") {
    const other = field.value === "outro";
    const container = field.form.querySelector("#other-neighborhood-field");
    if (container) {
      container.hidden = !other;
      container.querySelectorAll("input").forEach(input => { input.required = other; input.disabled = !other; });
    }
  }

  if (field.name === state.activeInterview?.questionnaireSnapshot?.field_context?.approach_type?.id) {
    const selectedMode = field.value;
    const duplicateLabel = field.form.querySelector("#duplicate-question-label");
    if (duplicateLabel) duplicateLabel.textContent = duplicateQuestion(selectedMode).label;
    field.form.querySelectorAll("[data-context-mode]").forEach(container => {
      const active = container.dataset.contextMode === selectedMode;
      container.hidden = !active;
      container.querySelectorAll("input, select, textarea").forEach(input => {
        input.disabled = !active;
      });
    });
  }

  const question = field.closest(".likert-question");
  if (question && field.type === "radio") {
    question.classList.add("answered");
    const form = field.form;
    const questions = [...form.querySelectorAll(".likert-question")];
    const answered = questions.filter(item => item.querySelector("input:checked")).length;
    const count = form.parentElement.querySelector("#psychometric-count");
    const progress = form.parentElement.querySelector("#psychometric-progress");
    if (count) count.textContent = `${answered} de ${questions.length} respondidas`;
    if (progress) progress.style.width = `${questions.length ? (answered / questions.length) * 100 : 0}%`;
  }

  scheduleDraftAutosave(0);
});

function updateConnectionIndicators() {
  document.querySelectorAll(".connection-badge").forEach(element => {
    element.classList.toggle("offline", !navigator.onLine);
    const label = element.querySelector("span");
    if (label) label.textContent = navigator.onLine ? "Online" : "Offline";
  });
}

async function attemptAutomaticSync() {
  if (!canRunSurveys() || state.session?.offline || !navigator.onLine || state.busy) return;
  const settings = await getSettings();
  if (!settings.autoSync) return;
  const summary = await dashboardSummary();
  if (!summary.pending) return;
  state.busy = true;
  try {
    state.syncResult = await synchronizePending(settings, state.experiment);
    if (state.syncResult.errors) console.warn(`${state.syncResult.errors} envio(s) permaneceram na fila.`);
  } catch (error) {
    console.warn("Sincronização automática adiada:", error);
  } finally {
    state.busy = false;
    if (["researcher-home", "survey-complete", "sync"].includes(state.route)) await render({ preserveScroll: true });
  }
}

function stopBackgroundJobs() {
  adminRealtimeGeneration += 1;
  if (adminRefreshTimer) clearInterval(adminRefreshTimer);
  if (autoSyncTimer) clearInterval(autoSyncTimer);
  if (adminRealtimeSubscription) {
    supabase.removeChannel(adminRealtimeSubscription).catch(() => {});
  }
  adminRealtimeSubscription = null;
  state.adminRealtimeActive = false;
  adminRefreshTimer = null;
  autoSyncTimer = null;
}

async function startAdminRealtime() {
  const generation = adminRealtimeGeneration;
  try {
    const subscription = supabase
      .channel("pdapp-admin-interviews")
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: supabaseConfig.interviewsTable
      }, () => {
        if (isAdminPortalRole() && state.route.startsWith("admin-") && !state.adminDashboardLoading) {
          refreshAdminDashboard({ background: true });
        }
      })
      .subscribe(status => {
        if (generation !== adminRealtimeGeneration || !isAdminPortalRole()) {
          supabase.removeChannel(subscription).catch(() => {});
          return;
        }
        state.adminRealtimeActive = status === "SUBSCRIBED";
        if (state.route.startsWith("admin-")) render({ preserveScroll: true });
      });
    adminRealtimeSubscription = subscription;
  } catch (error) {
    state.adminRealtimeActive = false;
    console.warn("Atualização em tempo real indisponível; usando verificação periódica.", error);
  }
}

function startBackgroundJobs() {
  stopBackgroundJobs();
  adminRealtimeGeneration += 1;
  if (isAdminPortalRole()) {
    startAdminRealtime();
    adminRefreshTimer = setInterval(() => {
      if (state.route.startsWith("admin-") && navigator.onLine && !state.adminDashboardLoading && !state.busy) {
        refreshAdminDashboard({ background: true });
      }
    }, ADMIN_REFRESH_INTERVAL_MS);
  }
  if (canRunSurveys()) {
    autoSyncTimer = setInterval(attemptAutomaticSync, 30_000);
  }
}

globalThis.addEventListener("beforeinstallprompt", event => {
  event.preventDefault();
  state.installPrompt = event;
  state.installHelpVisible = false;
  if (["login", "researcher-home"].includes(state.route)) render({ preserveScroll: true });
});

globalThis.addEventListener("appinstalled", () => {
  state.installPrompt = null;
  state.installHelpVisible = false;
  showToast("Aplicativo instalado com sucesso.", "success");
  render({ preserveScroll: true });
});

globalThis.addEventListener("online", async () => {
  updateConnectionIndicators();
  if (state.session?.offline) {
    const restored = await restoreSession();
    if (!restored) {
      state.session = null;
      stopBackgroundJobs();
      showToast("Sua sessão expirou. Entre novamente para continuar sincronizando.", "error");
      await navigate("login");
      return;
    }
    state.session = restored;
    startBackgroundJobs();
  }
  if (canRunSurveys()) attemptAutomaticSync();
  if (isAdminPortalRole() && state.route.startsWith("admin-")) refreshAdminDashboard({ background: true });
});
globalThis.addEventListener("offline", updateConnectionIndicators);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden" && state.activeInterview) {
    flushDraftAutosave().catch(error => console.error("Salvamento ao sair da tela:", error));
  }
});

async function initialize() {
  try {
    applyTheme();
    await openDatabase();
    await migrateLegacyDatabase();
    await recoverInterruptedSync();
    [state.experiment, state.questionnaire, state.neighborhoodCatalog, state.session, state.backendHealth] = await Promise.all([
      loadJson("./config/experimento.json"),
      loadJson("./config/questionario.json"),
      loadJson("./config/bairros.json"),
      restoreSession(),
      supabasePing
    ]);
    state.route = state.session
      ? (state.session.mustChangePassword ? "change-password" : (isAdminPortalRole() ? "admin-overview" : "researcher-home"))
      : "login";
    await render();

    if (state.session) startBackgroundJobs();
    if (isAdminPortalRole() && !state.session.mustChangePassword) await refreshAdminDashboard();
    if (canRunSurveys() && !state.session.mustChangePassword) attemptAutomaticSync();

    if ("serviceWorker" in navigator) {
      let refreshingForUpdate = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (refreshingForUpdate) return;
        refreshingForUpdate = true;
        globalThis.location.reload();
      });
      navigator.serviceWorker.register("./sw.js", { updateViaCache: "none" })
        .then(registration => registration.update())
        .catch(error => console.error("Service worker:", error));
    }
  } catch (error) {
    console.error(error);
    app.innerHTML = `<main class="startup-screen"><div class="brand-mark">!</div><h1>Falha ao iniciar</h1><p>${escapeHtml(error.message)}</p><button class="button" type="button" data-action="reload">Tentar novamente</button></main>`;
  }
}

initialize();
